import { FC, Fragment, FormEvent, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Dropdown, Form, ListGroup, Modal, Nav, Row, Spinner, Tab, Table } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CaresPageHeader from '../../shell/cares-page-header';
import { CompanyResourceBulkSetup } from '../../admin-settings/company-resource-spreadsheet-import';
import SpkDropdown from '../../../../shared/@spk-reusable-components/reusable-uielements/spk-dropdown';
import { getCaresShellBootstrap } from '../../shell/bootstrap';
import {
  createCompany,
  archiveCompany,
  createCompanyGroup,
  createCompanyLocation,
  createCompanyUser,
  updateCompanyUserPassword,
  deleteCompanyGroup,
  deleteCompanyLocation,
  deleteCompanyUser,
  fetchCompanies,
  fetchCompanyGroups,
  fetchCompanyLocations,
  fetchCompanyProfile,
  fetchCompanyUsers,
  permanentlyDeleteCompany,
  restoreCompany,
  updateCompanyGroup,
  updateCompanyLocation,
  updateCompanyProfile,
  updateCompanyUser,
  type CompanyGroupRecord,
  type CompanyIndexResponse,
  type CompanyLocationRecord,
  type CompanyProfile,
  type CompanyRecord,
  type CompanyStatus,
  type CompanyUserRecord,
  type CompanyUsersResponse,
} from './api';

type CompanyTabKey =
  | 'company-info'
  | 'locations'
  | 'teams-groups'
  | 'users'
  | 'roles-permissions'
  | 'schedule-settings'
  | 'billing-contract';

type SummaryMetric = {
  label: string;
  value: string;
  helper: string;
  icon: string;
  tone: string;
  trend: string;
};

type AddressBlock = {
  label: string;
  lines: string[];
};

type SelectOption = {
  value: string;
  label: string;
};

type PlaceholderTabContent = {
  key: Exclude<CompanyTabKey, 'company-info' | 'locations' | 'teams-groups' | 'users' | 'roles-permissions'>;
  label: string;
  helper: string;
  statusTone: 'success' | 'warning' | 'secondary';
  statusLabel: string;
  primaryAction?: {
    label: string;
    to: string;
  };
  columns: Array<{ key: string; label: string }>;
  rows: Array<Record<string, string>>;
  availabilityTitle: string;
  availabilityNote: string;
};

export type CompanyProfileFormState = {
  primary_domain: string;
  website_url: string;
  support_email: string;
  support_phone: string;
  notes: string;
  headquarters_address_line_1: string;
  headquarters_address_line_2: string;
  headquarters_city: string;
  headquarters_state: string;
  headquarters_postal_code: string;
  headquarters_country: string;
  operations_address_line_1: string;
  operations_address_line_2: string;
  operations_city: string;
  operations_state: string;
  operations_postal_code: string;
  operations_country: string;
  billing_contact_name: string;
  billing_address_line_1: string;
  billing_address_line_2: string;
  billing_city: string;
  billing_state: string;
  billing_postal_code: string;
  billing_country: string;
};

export type BrandingDraftState = {
  logoFile: File | null;
  logoMarkFile: File | null;
  logoMarkUrl: string;
  removeLogo: boolean;
  removeLogoMark: boolean;
};

type CompanyRegistryFormState = {
  name: string;
  slug: string;
};

export type CompanyLocationFormState = {
  id: number | null;
  code: string;
  display_name: string;
  public_label: string;
  location_group: string;
  timezone: string;
  is_active: boolean;
  notes: string;
};

export type CompanyGroupFormState = {
  id: number | null;
  parent_group_id: number | null;
  name: string;
  slug: string;
  kind: string;
  status: 'active' | 'inactive' | 'archived';
};

export type FieldErrors = Partial<Record<keyof CompanyProfileFormState, string>>;
export type LocationFieldErrors = Partial<Record<keyof CompanyLocationFormState, string>>;
export type GroupFieldErrors = Partial<Record<keyof CompanyGroupFormState, string>>;
type CompanyRegistryFieldErrors = Partial<Record<keyof CompanyRegistryFormState, string>>;
type UserStatus = 'invited' | 'active' | 'suspended';
type UserAccessLevel = 'staff' | 'group_schedule_admin' | 'company_admin' | 'company_admin_group_schedule_admin';
type GroupAccessRole = '' | 'member' | 'scheduler_admin';

type GroupAccessDraft = {
  group_id: number;
  role_key: GroupAccessRole;
};

type PasswordDraftState = {
  membershipId: number | null;
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
};

type PasswordFieldErrors = Partial<Record<'password' | 'password_confirmation', string>>;

type CompanyUserFormState = {
  id: number | null;
  email: string;
  name: string;
  status: UserStatus;
  access_level: UserAccessLevel;
  group_access: GroupAccessDraft[];
};

type UserFieldErrors = Partial<Record<'email' | 'name' | 'status' | 'company_role_keys' | 'group_memberships', string>>;

type SchedulerAssignmentSummary = {
  membershipId: number;
  userName: string;
  userEmail: string;
  membershipStatus: CompanyUserRecord['status'];
  groupMembershipId: number;
  groupName: string;
  groupKind: string | null;
  groupStatus: CompanyUserRecord['status'] | CompanyGroupRecord['status'];
  lastActivity: string | null | undefined;
};

type CompanyListLoadState = 'idle' | 'loaded' | 'failed';
type CompanyProfileLoadState = 'idle' | 'loading' | 'loaded' | 'failed';
type CompanyResourceKey = 'locations' | 'groups' | 'users';
type CompanyResourceLoadState = 'idle' | 'loading' | 'loaded' | 'failed';

type CompanyProfileStatus = {
  state: CompanyProfileLoadState;
  error: string | null;
  hasLoadedOnce: boolean;
};

type CompanyResourceStatus = {
  state: CompanyResourceLoadState;
  error: string | null;
  hasLoadedOnce: boolean;
};

const companyStatusToneMap: Record<CompanyStatus, string> = {
  draft: 'secondary',
  active: 'success',
  onboarding: 'warning',
  archived: 'dark',
};

const companyResourceLabels: Record<CompanyResourceKey, string> = {
  locations: 'Locations',
  groups: 'Departments & Groups',
  users: 'Users',
};

const companyTabOrder: CompanyTabKey[] = [
  'company-info',
  'locations',
  'teams-groups',
  'users',
  'roles-permissions',
  'schedule-settings',
  'billing-contract',
];

const companyTabLabels: Record<CompanyTabKey, string> = {
  'company-info': 'Company Info',
  locations: 'Locations',
  'teams-groups': 'Departments & Groups',
  users: 'Users',
  'roles-permissions': 'Roles / Permissions',
  'schedule-settings': 'Scheduling',
  'billing-contract': 'Billing / Contract',
};

const statusLabelMap: Record<CompanyStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  onboarding: 'Onboarding',
  archived: 'Archived',
};

const companyQueryTabMap: Record<string, CompanyTabKey> = {
  'company-info': 'company-info',
  company: 'company-info',
  groups: 'teams-groups',
  locations: 'locations',
  'roles-permissions': 'roles-permissions',
  roles: 'roles-permissions',
  'schedule-settings': 'schedule-settings',
  'teams-groups': 'teams-groups',
  users: 'users',
  'billing-contract': 'billing-contract',
  billing: 'billing-contract',
};

export const emptyProfileFormState: CompanyProfileFormState = {
  primary_domain: '',
  website_url: '',
  support_email: '',
  support_phone: '',
  notes: '',
  headquarters_address_line_1: '',
  headquarters_address_line_2: '',
  headquarters_city: '',
  headquarters_state: '',
  headquarters_postal_code: '',
  headquarters_country: '',
  operations_address_line_1: '',
  operations_address_line_2: '',
  operations_city: '',
  operations_state: '',
  operations_postal_code: '',
  operations_country: '',
  billing_contact_name: '',
  billing_address_line_1: '',
  billing_address_line_2: '',
  billing_city: '',
  billing_state: '',
  billing_postal_code: '',
  billing_country: '',
};

export const emptyBrandingDraftState: BrandingDraftState = {
  logoFile: null,
  logoMarkFile: null,
  logoMarkUrl: '',
  removeLogo: false,
  removeLogoMark: false,
};

const emptyCompanyRegistryFormState: CompanyRegistryFormState = {
  name: '',
  slug: '',
};

const locationGroupOptions: SelectOption[] = [
  { value: 'Primary', label: 'Primary / Default' },
  { value: 'Hospital', label: 'Hospital' },
  { value: 'Clinic', label: 'Clinic' },
  { value: 'Ambulatory', label: 'Ambulatory' },
  { value: 'Administrative', label: 'Administrative' },
  { value: 'Virtual', label: 'Virtual' },
];

export const emptyLocationFormState: CompanyLocationFormState = {
  id: null,
  code: '',
  display_name: '',
  public_label: '',
  location_group: 'Primary',
  timezone: 'America/New_York',
  is_active: true,
  notes: '',
};

export const emptyGroupFormState: CompanyGroupFormState = {
  id: null,
  parent_group_id: null,
  name: '',
  slug: '',
  kind: 'team',
  status: 'active',
};

const emptyPasswordDraftState: PasswordDraftState = {
  membershipId: null,
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
};

const emptyCompanyResourceStatus = (): Record<CompanyResourceKey, CompanyResourceStatus> => ({
  locations: {
    state: 'idle',
    error: null,
    hasLoadedOnce: false,
  },
  groups: {
    state: 'idle',
    error: null,
    hasLoadedOnce: false,
  },
  users: {
    state: 'idle',
    error: null,
    hasLoadedOnce: false,
  },
});

const emptyCompanyProfileStatus = (): CompanyProfileStatus => ({
  state: 'idle',
  error: null,
  hasLoadedOnce: false,
});

const emptyUserFormState = (groups: CompanyGroupRecord[]): CompanyUserFormState => ({
  id: null,
  email: '',
  name: '',
  status: 'invited',
  access_level: 'staff',
  group_access: groups.map((group) => ({
    group_id: group.id,
    role_key: '',
  })),
});

const addressLines = (lines: Array<string | null | undefined>) => {
  const normalized = lines.map((line) => line?.trim() ?? '').filter((line) => line.length > 0);
  return normalized.length > 0 ? normalized : ['Not provided'];
};

const formatCityStatePostal = (city?: string | null, state?: string | null, postalCode?: string | null) =>
  [city, state, postalCode].filter((value) => value && value.trim().length > 0).join(', ');

const formatDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'Not yet';

const toInitials = (name: string) =>
  name
    .split(/\s+/)
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const sortLocations = (records: CompanyLocationRecord[]) =>
  [...records].sort((left, right) => left.display_name.localeCompare(right.display_name));

const sortGroups = (records: CompanyGroupRecord[]) =>
  [...records].sort((left, right) => {
    const byType = Number(right.is_department) - Number(left.is_department);
    if (byType !== 0) return byType;

    const byParent = (left.parent_name ?? '').localeCompare(right.parent_name ?? '');
    return byParent !== 0 ? byParent : left.name.localeCompare(right.name);
  });

const sortUsers = (records: CompanyUserRecord[]) =>
  [...records].sort((left, right) =>
    (left.user?.name ?? left.user?.email ?? '').localeCompare(right.user?.name ?? right.user?.email ?? '')
  );

const userHasCompanyAdmin = (record: CompanyUserRecord) =>
  record.company_roles.some((role) => role.role_key === 'company_admin');

const userHasSchedulerAdmin = (record: CompanyUserRecord) =>
  record.group_memberships.some((membership) => membership.roles.some((role) => role.role_key === 'scheduler_admin'));

const userAccessLevelFromRecord = (record: CompanyUserRecord): UserAccessLevel => {
  const isCompanyAdmin = userHasCompanyAdmin(record);
  const isSchedulerAdmin = userHasSchedulerAdmin(record);

  if (isCompanyAdmin && isSchedulerAdmin) {
    return 'company_admin_group_schedule_admin';
  }

  if (isCompanyAdmin) {
    return 'company_admin';
  }

  if (isSchedulerAdmin) {
    return 'group_schedule_admin';
  }

  return 'staff';
};

const accessLevelIncludesSchedulerAdmin = (accessLevel: UserAccessLevel) =>
  accessLevel === 'group_schedule_admin' || accessLevel === 'company_admin_group_schedule_admin';

const buildMetrics = (record: CompanyRecord): SummaryMetric[] => [
  {
    label: 'Active Users',
    value: String(record.counts.memberships_active),
    helper: `${record.counts.memberships_total} total memberships`,
    icon: 'group-line',
    tone: 'primary',
    trend: record.counts.memberships_active > 0 ? 'Provisioned' : 'Needs users',
  },
  {
    label: 'Departments / Groups',
    value: String(record.counts.groups_total),
    helper: 'Organizational and scheduling units',
    icon: 'node-tree',
    tone: 'success',
    trend: record.counts.groups_total > 0 ? 'Scoped' : 'Build hierarchy',
  },
  {
    label: 'Locations',
    value: String(record.counts.locations_total),
    helper: 'Facilities and coverage sites',
    icon: 'map-pin-2-line',
    tone: 'secondary',
    trend: record.counts.locations_total > 0 ? 'Mapped' : 'Add sites',
  },
  {
    label: 'Providers',
    value: String(record.counts.providers_total),
    helper: `${record.counts.services_total} services linked`,
    icon: 'stethoscope-line',
    tone: 'warning',
    trend: record.counts.providers_total > 0 ? 'Roster ready' : 'Roster pending',
  },
];

const buildAddresses = (profile: CompanyProfile): AddressBlock[] => [
  {
    label: 'Company Address',
    lines: addressLines([
      profile.addresses.headquarters.address_line_1,
      profile.addresses.headquarters.address_line_2,
      formatCityStatePostal(
        profile.addresses.headquarters.city,
        profile.addresses.headquarters.state,
        profile.addresses.headquarters.postal_code
      ),
      profile.addresses.headquarters.country,
    ]),
  },
];

const buildPlaceholderTabs = (record: CompanyRecord): PlaceholderTabContent[] => [
  {
    key: 'schedule-settings',
    label: 'Scheduling',
    helper: 'Schedule defaults, approval thresholds, and staffing-policy configuration.',
    statusTone: canOpenScheduleSettings(record) ? 'success' : 'warning',
    statusLabel: canOpenScheduleSettings(record) ? 'Open in the schedule settings workspace' : 'Requires schedule-settings access',
    primaryAction: canOpenScheduleSettings(record)
      ? {
          label: 'Open schedule settings',
          to: `/scheduling/overview?company=${encodeURIComponent(record.slug)}`,
        }
      : undefined,
    columns: [
      { key: 'signal', label: 'Signal' },
      { key: 'value', label: 'Current value' },
      { key: 'state', label: 'Status' },
    ],
    rows: [
      {
        signal: 'Services linked',
        value: String(record.counts.services_total),
        state: record.counts.services_total > 0 ? 'Ready for deeper settings' : 'No services linked yet',
      },
      {
        signal: 'Provider roster',
        value: String(record.counts.providers_total),
        state: record.counts.providers_total > 0 ? 'Roster exists' : 'No provider profiles yet',
      },
    ],
    availabilityTitle: canOpenScheduleSettings(record) ? 'Open the live schedule workspace' : 'Schedule settings require a different operator session',
    availabilityNote: canOpenScheduleSettings(record)
      ? 'Use the live Scheduling workspace to manage tenant-specific locations, services, providers, and readiness details for this company.'
      : 'This company cannot open Scheduling from the current session. Use a platform-admin or scheduler-scoped login that has scheduling access for this tenant.',
  },
  {
    key: 'billing-contract',
    label: 'Billing / Contract',
    helper: 'Commercial terms, renewal dates, and service entitlements that affect platform setup.',
    statusTone: 'secondary',
    statusLabel: 'Read-only operator summary',
    columns: [
      { key: 'signal', label: 'Signal' },
      { key: 'value', label: 'Current value' },
      { key: 'state', label: 'Status' },
    ],
    rows: [
      {
        signal: 'Support contact',
        value: record.counts.memberships_total > 0 ? 'Tenant activity detected' : 'No tenant activity yet',
        state: 'No billing tools in this workspace',
      },
      {
        signal: 'Delete eligibility',
        value: record.can_delete ? 'Can delete' : 'Blocked',
        state: record.can_delete ? 'No linked operational records' : record.delete_blockers[0] ?? 'Protected',
      },
    ],
    availabilityTitle: 'Billing actions are not part of the admin companies workspace',
    availabilityNote: 'Operators can review tenant activity and delete eligibility here, but contract edits, billing changes, and downloadable summaries are not available from this workspace.',
  },
];

const canOpenScheduleSettings = (record: CompanyRecord) => {
  const bootstrap = getCaresShellBootstrap();

  if (!bootstrap.navigation.scheduleSettings) {
    return false;
  }

  if (bootstrap.canAccessAdmin) {
    return true;
  }

  return bootstrap.tenantCompanies.some((company) => company.slug === record.slug);
};

export const profileToFormState = (profile: CompanyProfile): CompanyProfileFormState => ({
  primary_domain: profile.primary_domain ?? '',
  website_url: profile.website_url ?? '',
  support_email: profile.support_email ?? '',
  support_phone: profile.support_phone ?? '',
  notes: profile.notes ?? '',
  headquarters_address_line_1: profile.addresses.headquarters.address_line_1 ?? '',
  headquarters_address_line_2: profile.addresses.headquarters.address_line_2 ?? '',
  headquarters_city: profile.addresses.headquarters.city ?? '',
  headquarters_state: profile.addresses.headquarters.state ?? '',
  headquarters_postal_code: profile.addresses.headquarters.postal_code ?? '',
  headquarters_country: profile.addresses.headquarters.country ?? '',
  operations_address_line_1: profile.addresses.operations.address_line_1 ?? '',
  operations_address_line_2: profile.addresses.operations.address_line_2 ?? '',
  operations_city: profile.addresses.operations.city ?? '',
  operations_state: profile.addresses.operations.state ?? '',
  operations_postal_code: profile.addresses.operations.postal_code ?? '',
  operations_country: profile.addresses.operations.country ?? '',
  billing_contact_name: profile.addresses.billing.contact_name ?? '',
  billing_address_line_1: profile.addresses.billing.address_line_1 ?? '',
  billing_address_line_2: profile.addresses.billing.address_line_2 ?? '',
  billing_city: profile.addresses.billing.city ?? '',
  billing_state: profile.addresses.billing.state ?? '',
  billing_postal_code: profile.addresses.billing.postal_code ?? '',
  billing_country: profile.addresses.billing.country ?? '',
});

export const locationToFormState = (record: CompanyLocationRecord): CompanyLocationFormState => ({
  id: record.id,
  code: record.code,
  display_name: record.display_name,
  public_label: record.public_label ?? '',
  location_group: record.location_group ?? '',
  timezone: record.timezone ?? 'America/New_York',
  is_active: record.is_active,
  notes: record.notes ?? '',
});

export const groupToFormState = (record: CompanyGroupRecord): CompanyGroupFormState => ({
  id: record.id,
  parent_group_id: record.parent_group_id,
  name: record.name,
  slug: record.slug,
  kind: record.kind,
  status: record.status,
});

const userToFormState = (record: CompanyUserRecord, groups: CompanyGroupRecord[]): CompanyUserFormState => ({
  id: record.id,
  email: record.user?.email ?? '',
  name: record.user?.name ?? '',
  status: record.status,
  access_level: userAccessLevelFromRecord(record),
  group_access: groups.map((group) => {
    const membership = record.group_memberships.find((entry) => entry.group_id === group.id);
    const roleKey = membership?.roles[0]?.role_key;

    return {
      group_id: group.id,
      role_key: roleKey === 'member' || roleKey === 'scheduler_admin' ? roleKey : '',
    };
  }),
});

const extractValidationErrors = <T extends string>(validation: Record<string, string[]>, allowedKeys: T[]) => {
  const nextFieldErrors = {} as Partial<Record<T, string>>;

  for (const [key, messages] of Object.entries(validation)) {
    if (allowedKeys.includes(key as T)) {
      nextFieldErrors[key as T] = messages[0];
    }
  }

  return nextFieldErrors;
};

const roleBadgeClasses = (roleKey?: string | null) => {
  if (roleKey === 'scheduler_admin') {
    return 'bg-warning-transparent text-warning';
  }

  if (roleKey === 'company_admin') {
    return 'bg-primary-transparent text-primary';
  }

  if (roleKey === 'member') {
    return 'bg-success-transparent text-success';
  }

  return 'bg-light text-muted';
};

const buildSchedulerAssignments = (users: CompanyUserRecord[]): SchedulerAssignmentSummary[] =>
  users.flatMap((record) =>
    record.group_memberships.flatMap((membership) =>
      membership.roles
        .filter((role) => role.role_key === 'scheduler_admin')
        .map(() => ({
          membershipId: record.id,
          userName: record.user?.name ?? 'Pending user',
          userEmail: record.user?.email ?? 'No email on record',
          membershipStatus: record.status,
          groupMembershipId: membership.id,
          groupName: membership.group_name ?? 'Unnamed department',
          groupKind: membership.group_kind,
          groupStatus: membership.status,
          lastActivity: record.user?.last_login_at ?? record.activated_at,
        }))
    )
  );

const formatResourceLoadError = (resourceLabel: string, requestError: unknown) =>
  requestError instanceof Error ? requestError.message : `Unable to load ${resourceLabel.toLowerCase()}.`;

const ResourceLoadStateNotice: FC<{
  label: string;
  status: CompanyResourceStatus;
  onRetry: () => void;
}> = ({ label, status, onRetry }) => {
  if (status.state === 'failed' && !status.hasLoadedOnce) {
    return (
      <Alert variant="danger" className="mb-0 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <span>{status.error ?? `Unable to load ${label.toLowerCase()}.`}</span>
        <Button variant="danger" className="btn-wave" onClick={onRetry}>
          Retry loading {label.toLowerCase()}
        </Button>
      </Alert>
    );
  }

  if (status.error) {
    return (
      <Alert variant="warning" className="mb-4 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <span>{status.error}</span>
        <Button variant="warning" className="btn-wave" onClick={onRetry}>
          Retry loading {label.toLowerCase()}
        </Button>
      </Alert>
    );
  }

  return null;
};

const ProfileLoadStateNotice: FC<{
  status: CompanyProfileStatus;
  onRetry: () => void;
}> = ({ status, onRetry }) => {
  if (status.state === 'failed' && !status.hasLoadedOnce) {
    return (
      <Alert variant="danger" className="mb-4 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <span>{status.error ?? 'Unable to load the selected company profile.'}</span>
        <Button variant="danger" className="btn-wave" onClick={onRetry}>
          Retry loading company profile
        </Button>
      </Alert>
    );
  }

  if (status.error) {
    return (
      <Alert variant="warning" className="mb-4 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <span>{status.error}</span>
        <Button variant="warning" className="btn-wave" onClick={onRetry}>
          Retry loading company profile
        </Button>
      </Alert>
    );
  }

  return null;
};

const ClientMetricCard: FC<{ metric: SummaryMetric }> = ({ metric }) => (
  <Card className="custom-card overflow-hidden h-100">
    <Card.Body>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
        <div className="d-flex align-items-start">
          <span className={`avatar avatar-md avatar-rounded bg-${metric.tone}-transparent text-${metric.tone} me-3`}>
            <i className={`ri-${metric.icon} fs-18`}></i>
          </span>
          <div>
            <p className="fw-medium fs-14 mb-0">{metric.label}</p>
            <p className="mb-0 fs-12 op-7">{metric.helper}</p>
          </div>
        </div>
        <Badge className={`bg-${metric.tone}-transparent text-${metric.tone}`}>{metric.trend}</Badge>
      </div>
      <div className="d-flex align-items-end justify-content-between gap-3">
        <div>
          <h3 className="fw-semibold mb-0">{metric.value}</h3>
          <span className="text-muted fs-12">Current selected client</span>
        </div>
        <div className="avatar avatar-sm rounded-circle bg-light text-muted">
          <i className="ri-arrow-right-up-line"></i>
        </div>
      </div>
    </Card.Body>
  </Card>
);

const PlaceholderTab: FC<{ tab: PlaceholderTabContent }> = ({ tab }) => (
  <div className="d-flex flex-column gap-3">
    <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
      <div>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
          <h5 className="mb-0">{tab.label}</h5>
          <Badge bg="" className={`bg-${tab.statusTone}-transparent text-${tab.statusTone}`}>
            {tab.statusLabel}
          </Badge>
        </div>
        <p className="text-muted mb-0">{tab.helper}</p>
      </div>
      {tab.primaryAction ? (
        <div className="d-flex flex-wrap gap-2">
          <Button as={Link} to={tab.primaryAction.to} variant="primary" className="btn-wave">
            <i className="ri-arrow-right-line me-1"></i>
            {tab.primaryAction.label}
          </Button>
        </div>
      ) : null}
    </div>

    <Card className="custom-card border">
      <Card.Body className="p-0">
        <Table responsive hover className="text-nowrap mb-0 align-middle">
          <thead>
            <tr>
              {tab.columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tab.rows.map((row, index) => (
              <tr key={`${tab.key}-${index}`}>
                {tab.columns.map((column) => (
                  <td key={column.key}>{row[column.key] ?? '-'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>

    <div className="rounded border p-3 bg-light">
      <p className="fw-medium mb-1">{tab.availabilityTitle}</p>
      <p className="text-muted mb-0">{tab.availabilityNote}</p>
    </div>
  </div>
);

export const CompanyInfoTab: FC<{
  profile: CompanyProfile;
  formState: CompanyProfileFormState;
  brandingDraft: BrandingDraftState;
  fieldErrors: FieldErrors;
  saving: boolean;
  onChange: (field: keyof CompanyProfileFormState, value: string) => void;
  onBrandingFileChange: (field: 'logoFile' | 'logoMarkFile', file: File | null) => void;
  onBrandingUrlChange: (value: string) => void;
  onBrandingRemoveChange: (field: 'removeLogo' | 'removeLogoMark', value: boolean) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}> = ({
  profile,
  formState,
  brandingDraft,
  fieldErrors,
  saving,
  onChange,
  onBrandingFileChange,
  onBrandingUrlChange,
  onBrandingRemoveChange,
  onSubmit,
}) => (
  <Form onSubmit={onSubmit}>
    <div className="d-flex flex-column gap-3">
      <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
        <div>
          <h5 className="mb-1">Company Info</h5>
          <p className="text-muted mb-0">Core identity, contact, and operational defaults for the selected client.</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button variant="primary" className="btn-wave" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save company info'}
          </Button>
        </div>
      </div>

      <Card className="custom-card border">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Legal Name</Form.Label>
                <Form.Control value={profile.name} readOnly />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Client Slug</Form.Label>
                <Form.Control value={profile.slug} readOnly />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Control value={statusLabelMap[profile.status]} readOnly />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Primary Domain</Form.Label>
                <Form.Control
                  value={formState.primary_domain}
                  onChange={(event) => onChange('primary_domain', event.target.value)}
                  isInvalid={Boolean(fieldErrors.primary_domain)}
                  placeholder="client.example.org"
                />
                <Form.Control.Feedback type="invalid">{fieldErrors.primary_domain}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Website URL</Form.Label>
                <Form.Control
                  value={formState.website_url}
                  onChange={(event) => onChange('website_url', event.target.value)}
                  isInvalid={Boolean(fieldErrors.website_url)}
                  placeholder="https://client.example.org"
                />
                <Form.Control.Feedback type="invalid">{fieldErrors.website_url}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Primary Contact Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formState.support_email}
                  onChange={(event) => onChange('support_email', event.target.value)}
                  isInvalid={Boolean(fieldErrors.support_email)}
                  placeholder="primary.contact@client.example.org"
                />
                <Form.Control.Feedback type="invalid">{fieldErrors.support_email}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Primary Contact Number</Form.Label>
                <Form.Control
                  value={formState.support_phone}
                  onChange={(event) => onChange('support_phone', event.target.value)}
                  isInvalid={Boolean(fieldErrors.support_phone)}
                  placeholder="(555) 555-5555"
                />
                <Form.Control.Feedback type="invalid">{fieldErrors.support_phone}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={12}>
              <div className="border-top border-block-start-dashed pt-3 mt-2">
                <div className="d-flex flex-column flex-lg-row justify-content-between gap-2 mb-3">
                  <div>
                    <p className="fw-medium mb-1">Tenant Branding</p>
                    <p className="text-muted fs-13 mb-0">
Upload a horizontal tenant logo for the authenticated workspace. Add a collapsed mark by upload or by pasting a hosted logo URL.
                    </p>
                  </div>
                  <Badge bg="" className="bg-primary-transparent text-primary align-self-start">
                    Header + sidebar
                  </Badge>
                </div>
                <Row className="g-3">
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label>Tenant Logo</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(event) => onBrandingFileChange('logoFile', event.target.files?.[0] ?? null)}
                      />
                      <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
                        {profile.branding.logo_url ? (
                          <img
                            src={profile.branding.logo_url}
                            alt={`${profile.name} logo`}
                            style={{ maxHeight: '48px', maxWidth: '220px', objectFit: 'contain' }}
                          />
                        ) : (
                          <span className="text-muted fs-13">No tenant logo uploaded yet.</span>
                        )}
                        {brandingDraft.logoFile ? (
                          <span className="fs-13 text-muted">Selected: {brandingDraft.logoFile.name}</span>
                        ) : null}
                      </div>
                      <Form.Check
                        className="mt-2"
                        type="checkbox"
                        id="remove-company-logo"
                        label="Remove current tenant logo"
                        checked={brandingDraft.removeLogo}
                        disabled={!profile.branding.has_logo && brandingDraft.logoFile === null}
                        onChange={(event) => onBrandingRemoveChange('removeLogo', event.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label>Tenant Mark / Collapsed Icon</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(event) => onBrandingFileChange('logoMarkFile', event.target.files?.[0] ?? null)}
                      />
                      <Form.Control
                        className="mt-2"
                        type="url"
                        placeholder="Or paste a logo URL, e.g. https://example.com/logo-mark.png"
                        value={brandingDraft.logoMarkUrl}
                        onChange={(event) => onBrandingUrlChange(event.target.value)}
                      />
                      <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
                        {profile.branding.logo_mark_url ? (
                          <span className="text-muted fs-13 text-break">
                            Current mark URL is used in the company header above.
                          </span>
                        ) : (
                          <span className="text-muted fs-13">No tenant mark uploaded yet. The header falls back to the main logo.</span>
                        )}
                        {brandingDraft.logoMarkFile ? (
                          <span className="fs-13 text-muted">Selected: {brandingDraft.logoMarkFile.name}</span>
                        ) : null}
                        {brandingDraft.logoMarkUrl.trim() ? (
                          <span className="fs-13 text-muted text-break">URL: {brandingDraft.logoMarkUrl.trim()}</span>
                        ) : null}
                      </div>
                      <Form.Check
                        className="mt-2"
                        type="checkbox"
                        id="remove-company-logo-mark"
                        label="Remove current tenant mark"
                        checked={brandingDraft.removeLogoMark}
                        disabled={!profile.branding.has_logo_mark && brandingDraft.logoMarkFile === null && brandingDraft.logoMarkUrl.trim() === ''}
                        onChange={(event) => onBrandingRemoveChange('removeLogoMark', event.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col md={12}>
              <div className="border-top border-block-start-dashed pt-3 mt-2">
                <div className="d-flex flex-column flex-lg-row justify-content-between gap-2 mb-3">
                  <div>
                    <p className="fw-medium mb-1">Company Address</p>
                    <p className="text-muted fs-13 mb-0">
                      This becomes the client’s default/primary location. Add branch sites from the Locations tab.
                    </p>
                  </div>
                  <Badge bg="" className="bg-primary-transparent text-primary align-self-start">
                    Syncs primary location
                  </Badge>
                </div>
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Address Line 1</Form.Label>
                      <Form.Control
                        value={formState.headquarters_address_line_1}
                        onChange={(event) => onChange('headquarters_address_line_1', event.target.value)}
                        isInvalid={Boolean(fieldErrors.headquarters_address_line_1)}
                        placeholder="Street address"
                      />
                      <Form.Control.Feedback type="invalid">{fieldErrors.headquarters_address_line_1}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Suite / Unit</Form.Label>
                      <Form.Control
                        value={formState.headquarters_address_line_2}
                        onChange={(event) => onChange('headquarters_address_line_2', event.target.value)}
                        isInvalid={Boolean(fieldErrors.headquarters_address_line_2)}
                        placeholder="Suite, floor, unit"
                      />
                      <Form.Control.Feedback type="invalid">{fieldErrors.headquarters_address_line_2}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        value={formState.headquarters_city}
                        onChange={(event) => onChange('headquarters_city', event.target.value)}
                        isInvalid={Boolean(fieldErrors.headquarters_city)}
                        placeholder="City"
                      />
                      <Form.Control.Feedback type="invalid">{fieldErrors.headquarters_city}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        value={formState.headquarters_state}
                        onChange={(event) => onChange('headquarters_state', event.target.value)}
                        isInvalid={Boolean(fieldErrors.headquarters_state)}
                        placeholder="State"
                      />
                      <Form.Control.Feedback type="invalid">{fieldErrors.headquarters_state}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Postal Code</Form.Label>
                      <Form.Control
                        value={formState.headquarters_postal_code}
                        onChange={(event) => onChange('headquarters_postal_code', event.target.value)}
                        isInvalid={Boolean(fieldErrors.headquarters_postal_code)}
                        placeholder="ZIP"
                      />
                      <Form.Control.Feedback type="invalid">{fieldErrors.headquarters_postal_code}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Country</Form.Label>
                      <Form.Control
                        value={formState.headquarters_country}
                        onChange={(event) => onChange('headquarters_country', event.target.value)}
                        isInvalid={Boolean(fieldErrors.headquarters_country)}
                        placeholder="Country"
                      />
                      <Form.Control.Feedback type="invalid">{fieldErrors.headquarters_country}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={formState.notes}
                  onChange={(event) => onChange('notes', event.target.value)}
                  isInvalid={Boolean(fieldErrors.notes)}
                  placeholder="Implementation notes, contact expectations, or tenant-specific context."
                />
                <Form.Control.Feedback type="invalid">{fieldErrors.notes}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  </Form>
);

export const LocationsTab: FC<{
  locations: CompanyLocationRecord[];
  busyRecordId: number | null;
  onCreate: () => void;
  onEdit: (record: CompanyLocationRecord) => void;
  onDelete: (record: CompanyLocationRecord) => void;
}> = ({ locations, busyRecordId, onCreate, onEdit, onDelete }) => (
  <div className="d-flex flex-column gap-3">
    <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
      <div>
        <h5 className="mb-1">Locations</h5>
        <p className="text-muted mb-0">
          Facility records used by scheduling imports, provider assignment, and downstream coverage rules.
        </p>
      </div>
      <div className="d-flex flex-wrap gap-2">
        <Button variant="primary" className="btn-wave" onClick={onCreate}>
          <i className="ri-add-line me-1"></i>
          Add location
        </Button>
      </div>
    </div>

    <Card className="custom-card border">
      <Card.Body className="p-0">
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Code</th>
              <th>Display Name</th>
              <th>Public Label</th>
              <th>Department</th>
              <th>Status</th>
              <th>Services</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {locations.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-5 text-muted">
                  No locations exist for this company yet.
                </td>
              </tr>
            ) : (
              locations.map((location) => (
                <tr key={location.id}>
                  <td className="fw-semibold">{location.code}</td>
                  <td>
                    <div>{location.display_name}</div>
                    {location.notes ? <div className="text-muted fs-12 mt-1">{location.notes}</div> : null}
                  </td>
                  <td>{location.public_label || '-'}</td>
                  <td>{location.location_group || '-'}</td>
                  <td>
                    <Badge bg={location.is_active ? 'success' : 'secondary'}>
                      {location.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td>{location.services_count}</td>
                  <td className="text-end">
                    <div className="d-inline-flex gap-2">
                      <Button size="sm" variant="light" className="btn-wave" onClick={() => onEdit(location)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="btn-wave"
                        disabled={busyRecordId === location.id}
                        onClick={() => onDelete(location)}
                      >
                        {busyRecordId === location.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  </div>
);

export const TeamsGroupsTab: FC<{
  groups: CompanyGroupRecord[];
  busyRecordId: number | null;
  onCreate: (kind: 'department' | 'team') => void;
  onEdit: (record: CompanyGroupRecord) => void;
  onDelete: (record: CompanyGroupRecord) => void;
}> = ({ groups, busyRecordId, onCreate, onEdit, onDelete }) => {
  type SortKey = 'name' | 'type' | 'status' | 'members' | 'children' | 'scheduler-admins';
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const rows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const departments = groups.filter((record) => record.is_department);
    const scheduleGroups = groups.filter((record) => !record.is_department);
    const childrenByDepartment = new Map<number, CompanyGroupRecord[]>();

    scheduleGroups.forEach((record) => {
      if (record.parent_group_id === null) return;
      const children = childrenByDepartment.get(record.parent_group_id) ?? [];
      children.push(record);
      childrenByDepartment.set(record.parent_group_id, children);
    });

    const searchableText = (record: CompanyGroupRecord) => [
      record.name,
      record.slug,
      record.display_kind,
      record.parent_name ?? 'Unassigned',
      record.status,
      ...(record.effective_scheduler_admins ?? []).flatMap((admin) => [admin.name, admin.email ?? '']),
    ].join(' ').toLowerCase();
    const matchesSearch = (record: CompanyGroupRecord) => normalizedSearch === '' || searchableText(record).includes(normalizedSearch);
    const valueForSort = (record: CompanyGroupRecord): string | number => {
      switch (sortKey) {
        case 'type': return record.display_kind;
        case 'status': return record.status;
        case 'members': return record.memberships_count;
        case 'children': return record.children_count;
        case 'scheduler-admins': return record.scheduler_admins_count ?? 0;
        default: return record.name;
      }
    };
    const compare = (left: CompanyGroupRecord, right: CompanyGroupRecord) => {
      const leftValue = valueForSort(left);
      const rightValue = valueForSort(right);
      const result = typeof leftValue === 'number' && typeof rightValue === 'number'
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? result : -result;
    };

    const hierarchyRows: Array<{ record: CompanyGroupRecord; depth: 0 | 1 }> = [];
    [...departments].sort(compare).forEach((department) => {
      const children = [...(childrenByDepartment.get(department.id) ?? [])].sort(compare);
      const matchingChildren = children.filter(matchesSearch);
      if (!matchesSearch(department) && matchingChildren.length === 0) return;
      hierarchyRows.push({ record: department, depth: 0 });
      (matchesSearch(department) ? children : matchingChildren).forEach((record) => hierarchyRows.push({ record, depth: 1 }));
    });

    scheduleGroups
      .filter((record) => record.parent_group_id === null && matchesSearch(record))
      .sort(compare)
      .forEach((record) => hierarchyRows.push({ record, depth: 0 }));

    return hierarchyRows;
  }, [groups, search, sortDirection, sortKey]);

  const requestSort = (nextKey: SortKey) => {
    if (nextKey === sortKey) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(nextKey);
    setSortDirection('asc');
  };

  const sortHeader = (label: string, key: SortKey) => (
    <button
      type="button"
      className="btn btn-link p-0 text-reset text-decoration-none fw-semibold"
      onClick={() => requestSort(key)}
      aria-label={`Sort by ${label}`}
    >
      {label}
      <i className={`ms-1 ${sortKey === key ? (sortDirection === 'asc' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line') : 'ri-expand-up-down-line'} text-muted`}></i>
    </button>
  );

  return (
  <div className="d-flex flex-column gap-3">
    <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
      <div>
        <h5 className="mb-1">Departments & Groups</h5>
        <p className="text-muted mb-0">
          Departments organize service lines and shared scheduler access. Schedule Groups are the independent units that own schedules, rules, requests, and publication.
        </p>
      </div>
      <div className="d-flex flex-wrap gap-2">
        <Button variant="outline-primary" className="btn-wave" onClick={() => onCreate('department')}>
          <i className="ri-add-line me-1"></i>
          Add Department
        </Button>
        <Button variant="primary" className="btn-wave" onClick={() => onCreate('team')}>
          <i className="ri-add-line me-1"></i>
          Add Schedule Group
        </Button>
      </div>
    </div>

    <Form.Group controlId="department-group-search">
      <Form.Label className="visually-hidden">Search Departments and Schedule Groups</Form.Label>
      <div className="position-relative">
        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
        <Form.Control
          type="search"
          className="ps-5"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search Department, Schedule Group, status, or Scheduler Admin"
        />
      </div>
    </Form.Group>

    <Card className="custom-card border">
      <Card.Body className="p-0">
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th>{sortHeader('Name', 'name')}</th>
              <th>{sortHeader('Type', 'type')}</th>
              <th>{sortHeader('Status', 'status')}</th>
              <th>{sortHeader('Members', 'members')}</th>
              <th>{sortHeader('Groups', 'children')}</th>
              <th>{sortHeader('Scheduler Admins', 'scheduler-admins')}</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-5 text-muted">
                  {groups.length === 0 ? 'No Departments or Schedule Groups exist for this client yet.' : 'No Departments or Schedule Groups match your search.'}
                </td>
              </tr>
            ) : (
              rows.map(({ record: group, depth }) => {
                const directAdmins = group.direct_scheduler_admins ?? [];
                const effectiveAdmins = group.effective_scheduler_admins ?? directAdmins;
                const inheritedOnly = depth === 1 && directAdmins.length === 0 && effectiveAdmins.length > 0;

                return (
                <tr key={group.id}>
                  <td>
                    <div className={`d-flex align-items-start gap-2 ${depth === 1 ? 'ps-4' : ''}`}>
                      <i className={`${group.is_department ? 'ri-building-2-line text-primary' : 'ri-corner-down-right-line text-muted'} mt-1`}></i>
                      <div>
                        <div className="fw-semibold">{group.name}</div>
                        {!group.is_department ? (
                          <div className="text-muted fs-12">{group.parent_name ? `Within ${group.parent_name}` : 'Unassigned Schedule Group'}</div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td>{group.display_kind}</td>
                  <td>
                    <Badge bg={group.status === 'active' ? 'success' : group.status === 'inactive' ? 'secondary' : 'dark'}>
                      {group.status}
                    </Badge>
                  </td>
                  <td>{group.memberships_count}</td>
                  <td>{group.is_department ? group.children_count : '—'}</td>
                  <td>
                    <div className="d-flex align-items-start gap-2">
                      <Badge bg={effectiveAdmins.length > 0 ? 'primary' : 'secondary'} pill>{effectiveAdmins.length}</Badge>
                      <div>
                        <div>{effectiveAdmins.length > 0 ? effectiveAdmins.map((admin) => admin.name).join(', ') : 'Not assigned'}</div>
                        {inheritedOnly ? <div className="text-muted fs-12">Inherited from Department</div> : null}
                        {directAdmins.length > 0 && depth === 1 ? <div className="text-muted fs-12">Direct Schedule Group access</div> : null}
                      </div>
                    </div>
                  </td>
                  <td className="text-end">
                    <div className="d-inline-flex gap-2">
                      <Button size="sm" variant="light" className="btn-wave" onClick={() => onEdit(group)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="btn-wave"
                        disabled={busyRecordId === group.id}
                        onClick={() => onDelete(group)}
                      >
                        {busyRecordId === group.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  </div>
  );
};

const UsersTab: FC<{
  usersSummary: CompanyUsersResponse['summary'] | null;
  users: CompanyUserRecord[];
  pendingInvites: CompanyUsersResponse['pending_invites'];
  busyRecordId: number | null;
  onCreate: () => void;
  onEdit: (record: CompanyUserRecord) => void;
  resettingRecordId: number | null;
  onPasswordUpdate: (record: CompanyUserRecord) => void;
  onDelete: (record: CompanyUserRecord) => void;
}> = ({ usersSummary, users, pendingInvites, busyRecordId, resettingRecordId, onCreate, onEdit, onPasswordUpdate, onDelete }) => {
  const schedulerAssignments = buildSchedulerAssignments(users);

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
        <div>
          <h5 className="mb-1">Users</h5>
          <p className="text-muted mb-0">
            Company memberships, invite state, and scoped role assignment for tenant operators and staff.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button variant="primary" className="btn-wave" onClick={onCreate}>
            <i className="ri-user-add-line me-1"></i>
            Invite user
          </Button>
        </div>
      </div>

      <Alert variant="info" className="mb-0">
        This workspace only changes access for the selected company. Scheduler admin assignments stay explicitly bound to the
        departments listed below and do not grant tenant-wide scheduling control by themselves.
      </Alert>

      <Row className="g-3">
        <Col md={3}>
          <div className="border border-dashed rounded p-3 h-100">
            <p className="text-muted mb-1">Memberships</p>
            <h4 className="mb-0">{usersSummary?.total ?? users.length}</h4>
          </div>
        </Col>
        <Col md={3}>
          <div className="border border-dashed rounded p-3 h-100">
            <p className="text-muted mb-1">Active</p>
            <h4 className="mb-0">{usersSummary?.active ?? 0}</h4>
          </div>
        </Col>
        <Col md={3}>
          <div className="border border-dashed rounded p-3 h-100">
            <p className="text-muted mb-1">Pending invites</p>
            <h4 className="mb-0">{usersSummary?.pending_invitations ?? pendingInvites.length}</h4>
          </div>
        </Col>
        <Col md={3}>
          <div className="border border-dashed rounded p-3 h-100">
            <p className="text-muted mb-1">Scheduler assignments</p>
            <h4 className="mb-0">{schedulerAssignments.length}</h4>
          </div>
        </Col>
      </Row>

      <Card className="custom-card border">
        <Card.Header className="d-flex flex-column flex-lg-row justify-content-between gap-2">
          <div>
            <div className="card-title mb-1">Memberships and department assignments</div>
            <div className="text-muted fs-13">
              Review tenant admins, employee membership state, and per-group scheduler scope from one table.
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th>User</th>
                <th>Invite / Membership</th>
                <th>Company Roles</th>
                <th>Department Access</th>
                <th>Last Activity</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-5 text-muted">
                    No company users are attached yet.
                  </td>
                </tr>
              ) : (
                users.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <div className="fw-semibold">{record.user?.name ?? 'Pending user'}</div>
                      <div className="text-muted fs-12">{record.user?.email ?? 'No email on record'}</div>
                    </td>
                    <td>
                      <Badge bg={record.status === 'active' ? 'success' : record.status === 'suspended' ? 'warning' : 'info'}>
                        {record.status}
                      </Badge>
                      <div className="text-muted fs-12 mt-2">Invited {formatDateTime(record.invited_at)}</div>
                      {record.status === 'active' ? (
                        <div className="text-muted fs-12">Activated {formatDateTime(record.activated_at)}</div>
                      ) : null}
                      {record.status === 'suspended' ? (
                        <div className="text-muted fs-12">Suspended {formatDateTime(record.suspended_at)}</div>
                      ) : null}
                    </td>
                    <td>
                      {record.company_roles.length === 0 ? (
                        <span className="text-muted">No company-wide role</span>
                      ) : (
                        <div className="d-flex flex-wrap gap-2">
                          {record.company_roles.map((role) => (
                            <Badge key={role.id} bg="" className={roleBadgeClasses(role.role_key)}>
                              {role.role_name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {record.group_memberships.length === 0 ? (
                        <span className="text-muted">No department access</span>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {record.group_memberships.map((membership) => (
                            <div key={membership.id} className="border rounded px-2 py-1">
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="fw-medium">{membership.group_name}</span>
                                <Badge bg={membership.status === 'active' ? 'success' : membership.status === 'suspended' ? 'warning' : 'info'}>
                                  {membership.status}
                                </Badge>
                                {membership.group_kind ? <span className="text-muted fs-12 text-capitalize">{membership.group_kind}</span> : null}
                              </div>
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {membership.roles.length === 0 ? (
                                  <span className="text-muted fs-12">No scoped role</span>
                                ) : (
                                  membership.roles.map((role) => (
                                    <Badge key={role.id} bg="" className={roleBadgeClasses(role.role_key)}>
                                      {role.role_name}
                                    </Badge>
                                  ))
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="text-muted fs-12">{formatDateTime(record.user?.last_login_at ?? record.activated_at)}</td>
                    <td className="text-center">
                      <div className="d-inline-flex justify-content-center gap-2">
                        <Button size="sm" variant="light" className="btn-wave" onClick={() => onEdit(record)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="btn-wave"
                          disabled={resettingRecordId === record.id || !record.user?.email}
                          onClick={() => onPasswordUpdate(record)}
                        >
                          {resettingRecordId === record.id ? 'Saving...' : 'Set Password'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="btn-wave"
                          disabled={busyRecordId === record.id}
                          onClick={() => onDelete(record)}
                        >
                          {busyRecordId === record.id ? 'Removing...' : 'Remove'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col xl={7}>
          <Card className="custom-card border h-100">
            <Card.Header>
              <div className="card-title mb-1">Invitation queue</div>
              <div className="text-muted fs-13">Pending invite records are separate from accepted memberships and remain tenant-bound.</div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Accepted</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvites.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        No pending invitation records for this company.
                      </td>
                    </tr>
                  ) : (
                    pendingInvites.map((invite) => (
                      <tr key={invite.id}>
                        <td>{invite.email}</td>
                        <td>
                          <Badge bg={invite.status === 'pending' ? 'info' : 'secondary'}>{invite.status}</Badge>
                        </td>
                        <td className="text-muted fs-12">{formatDateTime(invite.expires_at)}</td>
                        <td className="text-muted fs-12">{formatDateTime(invite.accepted_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={5}>
          <Card className="custom-card border h-100">
            <Card.Header>
              <div className="card-title mb-1">Scheduler scope</div>
              <div className="text-muted fs-13">Every scheduler admin grant is listed with its exact team boundary.</div>
            </Card.Header>
            <Card.Body className="d-flex flex-column gap-2">
              {schedulerAssignments.length === 0 ? (
                <div className="text-muted">No scheduler-admin department assignments are active for this company.</div>
              ) : (
                schedulerAssignments.map((assignment) => (
                  <div key={assignment.groupMembershipId} className="border rounded p-3">
                    <div className="d-flex flex-wrap justify-content-between gap-2">
                      <div>
                        <div className="fw-semibold">{assignment.userName}</div>
                        <div className="text-muted fs-12">{assignment.userEmail}</div>
                      </div>
                      <Badge bg={assignment.membershipStatus === 'active' ? 'success' : assignment.membershipStatus === 'suspended' ? 'warning' : 'info'}>
                        {assignment.membershipStatus}
                      </Badge>
                    </div>
                    <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                      <Badge bg="" className="bg-warning-transparent text-warning">
                        Scheduler admin
                      </Badge>
                      <span className="fw-medium">{assignment.groupName}</span>
                      {assignment.groupKind ? <span className="text-muted fs-12 text-capitalize">{assignment.groupKind}</span> : null}
                    </div>
                    <div className="text-muted fs-12 mt-2">
                      Department membership state: {assignment.groupStatus} · Last activity {formatDateTime(assignment.lastActivity)}
                    </div>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

type RoleMemberRow = {
  id: string;
  name: string;
  email: string;
  status: CompanyUserRecord['status'];
  roles: Array<{ key: string; label: string }>;
  scopes: string[];
  lastActivity?: string | null;
};

const RoleMembersTable: FC<{ rows: RoleMemberRow[] }> = ({ rows }) => (
  <Card className="custom-card border">
    <Card.Header className="d-flex flex-column flex-lg-row justify-content-between gap-2">
      <div>
        <div className="card-title mb-1">Members and access</div>
        <div className="text-muted fs-13">Each member appears once. Role and scope may contain multiple access assignments.</div>
      </div>
      <Badge bg="" className="bg-primary-transparent text-primary">{rows.length} unique members</Badge>
    </Card.Header>
    <Card.Body className="p-0">
      <Table responsive hover bordered className="mb-0 align-middle">
        <thead>
          <tr>
            <th>Member</th>
            <th>Status</th>
            <th>Role</th>
            <th>Scope</th>
            <th>Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center py-4 text-muted">No members assigned.</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="fw-semibold">{row.name}</div>
                  <div className="text-muted fs-12">{row.email}</div>
                </td>
                <td>
                  <Badge bg={row.status === 'active' ? 'success' : row.status === 'suspended' ? 'warning' : 'info'}>{row.status}</Badge>
                </td>
                <td>
                  <div className="d-flex flex-wrap gap-2">
                    {row.roles.map((role) => (
                      <Badge key={role.key} bg="" className={roleBadgeClasses(role.key)}>{role.label}</Badge>
                    ))}
                  </div>
                </td>
                <td>
                  <div className="d-flex flex-wrap gap-2">
                    {row.scopes.map((scope) => (
                      <Badge key={scope} bg="" className="bg-light text-default border">{scope}</Badge>
                    ))}
                  </div>
                </td>
                <td className="text-muted fs-12">{formatDateTime(row.lastActivity)}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Card.Body>
  </Card>
);

const RolesPermissionsTab: FC<{ users: CompanyUserRecord[] }> = ({ users }) => {
  const rows = Array.from(users.reduce((members, record) => {
    const memberKey = record.user ? `user-${record.user.id}` : `membership-${record.id}`;
    const existing = members.get(memberKey);
    const roleMap = new Map(existing?.roles.map((role) => [role.key, role]) ?? []);
    const scopes = new Set(existing?.scopes ?? []);

    record.company_roles.forEach((role) => {
      const key = role.role_key ?? `company-role-${role.id}`;
      roleMap.set(key, { key, label: role.role_name ?? role.role_key ?? 'Company role' });
      scopes.add('Company-wide');
    });

    record.group_memberships.forEach((membership) => {
      if (membership.roles.length === 0) return;

      scopes.add(membership.group_name ?? 'Unnamed Department or Schedule Group');
      membership.roles.forEach((role) => {
        const key = role.role_key ?? `group-role-${role.id}`;
        roleMap.set(key, { key, label: role.role_name ?? role.role_key ?? 'Scoped role' });
      });
    });

    members.set(memberKey, {
      id: memberKey,
      name: record.user?.name ?? existing?.name ?? 'Pending user',
      email: record.user?.email ?? existing?.email ?? 'No email on record',
      status: record.status,
      roles: Array.from(roleMap.values()),
      scopes: Array.from(scopes),
      lastActivity: record.user?.last_login_at ?? record.activated_at ?? existing?.lastActivity,
    });

    return members;
  }, new Map<string, RoleMemberRow>()).values());

  const companyAdminCount = rows.filter((row) => row.roles.some((role) => role.key === 'company_admin')).length;
  const schedulerAdminCount = rows.filter((row) => row.roles.some((role) => role.key === 'scheduler_admin')).length;

  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <h5 className="mb-1">Roles / Permissions</h5>
        <p className="text-muted mb-0">
          Assignment overview for this company. Change a member&apos;s access level from the Users tab by editing that user.
        </p>
      </div>

      <Row className="g-3">
        <Col md={4}>
          <div className="border border-dashed rounded p-3 h-100">
            <p className="text-muted mb-1">Unique Members</p>
            <h4 className="mb-0">{rows.length}</h4>
          </div>
        </Col>
        <Col md={4}>
          <div className="border border-dashed rounded p-3 h-100">
            <p className="text-muted mb-1">Company Admins</p>
            <h4 className="mb-0">{companyAdminCount}</h4>
          </div>
        </Col>
        <Col md={4}>
          <div className="border border-dashed rounded p-3 h-100">
            <p className="text-muted mb-1">Scheduler Admins</p>
            <h4 className="mb-0">{schedulerAdminCount}</h4>
          </div>
        </Col>
      </Row>

      <RoleMembersTable rows={rows} />
    </div>
  );
};

const CompanyRegistryModal: FC<{
  draft: CompanyRegistryFormState;
  fieldErrors: CompanyRegistryFieldErrors;
  saving: boolean;
  show: boolean;
  onChange: (field: keyof CompanyRegistryFormState, value: string) => void;
  onHide: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}> = ({ draft, fieldErrors, saving, show, onChange, onHide, onSubmit }) => (
  <Modal show={show} onHide={onHide} size="lg" centered>
    <Form onSubmit={onSubmit}>
      <Modal.Header closeButton>
        <Modal.Title>Provision New Client</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          Start the tenant lifecycle here, then move directly into company info, users, and group-scoped scheduler assignments after creation.
        </Alert>
        <Row className="g-3">
          <Col md={12}>
            <Form.Group>
              <Form.Label>Client / Company Name</Form.Label>
              <Form.Control
                value={draft.name}
                onChange={(event) => {
                  const nextName = event.target.value;
                  onChange('name', nextName);
                  onChange('slug', slugify(nextName));
                }}
                isInvalid={Boolean(fieldErrors.name)}
                placeholder="St. Catherine Medical Group"
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.name}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Client Slug</Form.Label>
              <Form.Control
                value={draft.slug}
                onChange={(event) => onChange('slug', slugify(event.target.value))}
                isInvalid={Boolean(fieldErrors.slug)}
                placeholder="st-catherine-medical-group"
              />
              <Form.Text className="text-muted">
                New clients begin as Draft. CARES derives Onboarding and Active from saved setup data, assigned users, and published schedules.
              </Form.Text>
              <Form.Control.Feedback type="invalid">{fieldErrors.slug}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="light" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Provisioning...' : 'Create client'}
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>
);

export const LocationModal: FC<{
  draft: CompanyLocationFormState;
  fieldErrors: LocationFieldErrors;
  saving: boolean;
  show: boolean;
  onChange: (field: keyof CompanyLocationFormState, value: string | boolean) => void;
  onHide: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}> = ({ draft, fieldErrors, saving, show, onChange, onHide, onSubmit }) => (
  <Modal show={show} onHide={onHide} size="lg" centered>
    <Form onSubmit={onSubmit}>
      <Modal.Header closeButton>
        <Modal.Title>{draft.id === null ? 'Add Location' : 'Edit Location'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Code</Form.Label>
              <Form.Control
                value={draft.code}
                onChange={(event) => onChange('code', event.target.value.toUpperCase())}
                isInvalid={Boolean(fieldErrors.code)}
                placeholder="MAIN"
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.code}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group>
              <Form.Label>Display Name</Form.Label>
              <Form.Control
                value={draft.display_name}
                onChange={(event) => onChange('display_name', event.target.value)}
                isInvalid={Boolean(fieldErrors.display_name)}
                placeholder="Main Campus"
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.display_name}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Public Label</Form.Label>
              <Form.Control
                value={draft.public_label}
                onChange={(event) => onChange('public_label', event.target.value)}
                isInvalid={Boolean(fieldErrors.public_label)}
                placeholder="Main Campus Hospital"
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.public_label}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Location Category</Form.Label>
              <SpkDropdown
                Togglevariant="outline-primary"
                Customclass="w-100"
                Customtoggleclass="custom-nft-btn w-100 d-flex align-items-center justify-content-between"
                Toggletext={locationGroupOptions.find((option) => option.value === draft.location_group)?.label ?? 'Select location category'}
                Menuclass="w-100"
              >
                {locationGroupOptions.map((option) => (
                  <Dropdown.Item
                    as="button"
                    type="button"
                    key={option.value}
                    active={draft.location_group === option.value}
                    onClick={() => {
                      onChange('location_group', option.value);
                    }}
                  >
                    {option.label}
                  </Dropdown.Item>
                ))}
              </SpkDropdown>
              {fieldErrors.location_group ? <div className="invalid-feedback d-block">{fieldErrors.location_group}</div> : null}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Timezone</Form.Label>
              <Form.Control
                value={draft.timezone}
                onChange={(event) => onChange('timezone', event.target.value)}
                isInvalid={Boolean(fieldErrors.timezone)}
                placeholder="America/New_York"
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.timezone}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6} className="d-flex align-items-end">
            <Form.Check
              type="switch"
              id="location-active-switch"
              checked={draft.is_active}
              onChange={(event) => onChange('is_active', event.target.checked)}
              label="Location is active"
            />
          </Col>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={draft.notes}
                onChange={(event) => onChange('notes', event.target.value)}
                isInvalid={Boolean(fieldErrors.notes)}
                placeholder="Operational notes for the site."
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.notes}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="light" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving...' : draft.id === null ? 'Create location' : 'Save location'}
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>
);

const UserModal: FC<{
  draft: CompanyUserFormState;
  groups: CompanyGroupRecord[];
  fieldErrors: UserFieldErrors;
  saving: boolean;
  show: boolean;
  onChange: (field: keyof Omit<CompanyUserFormState, 'group_access'>, value: string | number | null) => void;
  onGroupRoleChange: (groupId: number, roleKey: GroupAccessRole) => void;
  onHide: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}> = ({ draft, groups, fieldErrors, saving, show, onChange, onGroupRoleChange, onHide, onSubmit }) => (
  <Modal show={show} onHide={onHide} size="xl" centered>
    <Form onSubmit={onSubmit}>
      <Modal.Header closeButton>
        <Modal.Title>{draft.id === null ? 'Add Company User' : 'Edit Company User'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                value={draft.email}
                onChange={(event) => onChange('email', event.target.value)}
                isInvalid={Boolean(fieldErrors.email)}
                placeholder="nurse.manager@example.org"
                disabled={draft.id !== null}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.email}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Display name</Form.Label>
              <Form.Control
                value={draft.name}
                onChange={(event) => onChange('name', event.target.value)}
                isInvalid={Boolean(fieldErrors.name)}
                placeholder="Nurse Manager"
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.name}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Membership status</Form.Label>
              <Form.Select value={draft.status} onChange={(event) => onChange('status', event.target.value)}>
                <option value="invited">Invited</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group>
              <Form.Label>Access level</Form.Label>
              <Form.Select value={draft.access_level} onChange={(event) => onChange('access_level', event.target.value)}>
                <option value="staff">Staff</option>
                <option value="group_schedule_admin">Scheduler Admin</option>
                <option value="company_admin">Company Admin</option>
                <option value="company_admin_group_schedule_admin">Company Admin + Scheduler Admin</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Assign access at the Department level to inherit all child Schedule Groups, or choose an individual Schedule Group for narrower access.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <div className="rounded border p-3 mt-4">
          <div className="fw-medium mb-2">Department & Schedule Group access</div>
          <div className="text-muted fs-13 mb-3">
            Department access covers all of its current child Schedule Groups. A direct Schedule Group assignment covers only that scheduling unit.
          </div>
          {fieldErrors.group_memberships ? <Alert variant="danger" className="mb-3">{fieldErrors.group_memberships}</Alert> : null}
          <Row className="g-3">
            {groups.length === 0 ? (
              <Col md={12}>
                <Alert variant="warning" className="mb-0">
                  Create at least one Department or Schedule Group before assigning scoped scheduler or staff access.
                </Alert>
              </Col>
            ) : (
              groups.map((group) => {
                const currentDraft = draft.group_access.find((entry) => entry.group_id === group.id);

                return (
                  <Col md={6} key={group.id}>
                    <div className="border rounded p-3 h-100">
                      <div className="fw-semibold">{group.name}</div>
                      <div className="text-muted fs-12 mb-3">
                        {group.display_kind}{group.parent_name ? ` · ${group.parent_name}` : ''} · {group.status}
                      </div>
                      <Form.Select
                        value={currentDraft?.role_key ?? ''}
                        onChange={(event) => onGroupRoleChange(group.id, event.target.value as GroupAccessRole)}
                      >
                        <option value="">No access</option>
                        <option value="member">Staff</option>
                        <option value="scheduler_admin" disabled={!accessLevelIncludesSchedulerAdmin(draft.access_level)}>
                          Scheduler Admin
                        </option>
                      </Form.Select>
                    </div>
                  </Col>
                );
              })
            )}
          </Row>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="light" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving...' : draft.id === null ? 'Invite / assign user' : 'Save user access'}
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>
);

const PasswordModal: FC<{
  draft: PasswordDraftState;
  fieldErrors: PasswordFieldErrors;
  saving: boolean;
  show: boolean;
  onChange: (field: 'password' | 'password_confirmation', value: string) => void;
  onHide: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}> = ({ draft, fieldErrors, saving, show, onChange, onHide, onSubmit }) => {
  const hasMinLength = draft.password.length >= 8;
  const hasLetter = /[A-Za-z]/.test(draft.password);
  const hasNumber = /\d/.test(draft.password);
  const matches = draft.password !== '' && draft.password === draft.password_confirmation;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Set User Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Update the database password for <span className="fw-semibold">{draft.name || draft.email}</span>.
          </p>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>New password</Form.Label>
                <Form.Control
                  type="password"
                  value={draft.password}
                  onChange={(event) => onChange('password', event.target.value)}
                  isInvalid={Boolean(fieldErrors.password)}
                  autoComplete="new-password"
                />
                <Form.Control.Feedback type="invalid">{fieldErrors.password}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Confirm password</Form.Label>
                <Form.Control
                  type="password"
                  value={draft.password_confirmation}
                  onChange={(event) => onChange('password_confirmation', event.target.value)}
                  isInvalid={Boolean(fieldErrors.password_confirmation)}
                  autoComplete="new-password"
                />
                <Form.Control.Feedback type="invalid">{fieldErrors.password_confirmation}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <div className="rounded border p-3 mt-3 d-flex flex-column gap-2 fs-13">
            <span className={hasMinLength ? 'text-success' : 'text-muted'}>
              <i className={`${hasMinLength ? 'ri-checkbox-circle-line' : 'ri-checkbox-blank-circle-line'} me-1`}></i>
              At least 8 characters
            </span>
            <span className={hasLetter ? 'text-success' : 'text-muted'}>
              <i className={`${hasLetter ? 'ri-checkbox-circle-line' : 'ri-checkbox-blank-circle-line'} me-1`}></i>
              Contains a letter
            </span>
            <span className={hasNumber ? 'text-success' : 'text-muted'}>
              <i className={`${hasNumber ? 'ri-checkbox-circle-line' : 'ri-checkbox-blank-circle-line'} me-1`}></i>
              Contains a number
            </span>
            <span className={matches ? 'text-success' : 'text-muted'}>
              <i className={`${matches ? 'ri-checkbox-circle-line' : 'ri-checkbox-blank-circle-line'} me-1`}></i>
              Both entries match
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="light" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving || !hasMinLength || !hasLetter || !hasNumber || !matches}>
            {saving ? 'Updating...' : 'Update password'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const GroupModal: FC<{
  draft: CompanyGroupFormState;
  groups: CompanyGroupRecord[];
  fieldErrors: GroupFieldErrors;
  saving: boolean;
  show: boolean;
  onChange: (field: keyof CompanyGroupFormState, value: string | number | null) => void;
  onHide: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}> = ({ draft, groups, fieldErrors, saving, show, onChange, onHide, onSubmit }) => {
  const isDepartment = draft.kind === 'department';
  const recordLabel = isDepartment ? 'Department' : 'Schedule Group';
  const legacyKind = draft.kind !== 'department' && draft.kind !== 'team' ? draft.kind : null;

  return (
  <Modal show={show} onHide={onHide} size="lg" centered>
    <Form onSubmit={onSubmit}>
      <Modal.Header closeButton>
        <Modal.Title>{draft.id === null ? `Add ${recordLabel}` : `Edit ${recordLabel}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={draft.name}
                onChange={(event) => {
                  const nextName = event.target.value;
                  onChange('name', nextName);
                  if (draft.id === null || draft.slug === slugify(draft.name)) {
                    onChange('slug', slugify(nextName));
                  }
                }}
                isInvalid={Boolean(fieldErrors.name)}
                placeholder={isDepartment ? 'Department of Neurosciences' : 'Neurosurgery'}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.name}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Kind</Form.Label>
              <Form.Select
                value={draft.kind}
                onChange={(event) => onChange('kind', event.target.value)}
                isInvalid={Boolean(fieldErrors.kind)}
              >
                <option value="department">Department</option>
                <option value="team">Schedule Group</option>
                {legacyKind ? <option value={legacyKind}>Schedule Group (legacy: {legacyKind})</option> : null}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{fieldErrors.kind}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Slug</Form.Label>
              <Form.Control
                value={draft.slug}
                onChange={(event) => onChange('slug', slugify(event.target.value))}
                isInvalid={Boolean(fieldErrors.slug)}
                placeholder="icu-team-a"
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.slug}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={draft.status}
                onChange={(event) => onChange('status', event.target.value)}
                isInvalid={Boolean(fieldErrors.status)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{fieldErrors.status}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          {!isDepartment ? <Col md={12}>
            <Form.Group>
              <Form.Label>Parent Department</Form.Label>
              <Form.Select
                value={draft.parent_group_id ?? ''}
                onChange={(event) => onChange('parent_group_id', event.target.value === '' ? null : Number(event.target.value))}
                isInvalid={Boolean(fieldErrors.parent_group_id)}
              >
                <option value="">Unassigned</option>
                {groups
                  .filter((group) => group.is_department && group.id !== draft.id)
                  .map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{fieldErrors.parent_group_id}</Form.Control.Feedback>
            </Form.Group>
          </Col> : null}
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="light" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving...' : draft.id === null ? `Create ${recordLabel.toLowerCase()}` : `Save ${recordLabel.toLowerCase()}`}
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>
  );
};

const ClientsPage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [records, setRecords] = useState<CompanyRecord[]>([]);
  const [summary, setSummary] = useState<CompanyIndexResponse['summary'] | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<CompanyTabKey>('company-info');
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [formState, setFormState] = useState<CompanyProfileFormState>(emptyProfileFormState);
  const [brandingDraft, setBrandingDraft] = useState<BrandingDraftState>(emptyBrandingDraftState);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [locations, setLocations] = useState<CompanyLocationRecord[]>([]);
  const [groups, setGroups] = useState<CompanyGroupRecord[]>([]);
  const [users, setUsers] = useState<CompanyUserRecord[]>([]);
  const [usersSummary, setUsersSummary] = useState<CompanyUsersResponse['summary'] | null>(null);
  const [pendingInvites, setPendingInvites] = useState<CompanyUsersResponse['pending_invites']>([]);
  const [companyRegistryDraft, setCompanyRegistryDraft] = useState<CompanyRegistryFormState>(emptyCompanyRegistryFormState);
  const [locationDraft, setLocationDraft] = useState<CompanyLocationFormState>(emptyLocationFormState);
  const [groupDraft, setGroupDraft] = useState<CompanyGroupFormState>(emptyGroupFormState);
  const [userDraft, setUserDraft] = useState<CompanyUserFormState>(emptyUserFormState([]));
  const [passwordDraft, setPasswordDraft] = useState<PasswordDraftState>(emptyPasswordDraftState);
  const [companyRegistryFieldErrors, setCompanyRegistryFieldErrors] = useState<CompanyRegistryFieldErrors>({});
  const [locationFieldErrors, setLocationFieldErrors] = useState<LocationFieldErrors>({});
  const [groupFieldErrors, setGroupFieldErrors] = useState<GroupFieldErrors>({});
  const [userFieldErrors, setUserFieldErrors] = useState<UserFieldErrors>({});
  const [passwordFieldErrors, setPasswordFieldErrors] = useState<PasswordFieldErrors>({});
  const [showCompanyRegistryModal, setShowCompanyRegistryModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteCompanyModal, setShowDeleteCompanyModal] = useState(false);
  const [deleteCompanyName, setDeleteCompanyName] = useState('');
  const [deleteCompanyReason, setDeleteCompanyReason] = useState('');
  const [deleteCompanyAcknowledged, setDeleteCompanyAcknowledged] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingCompanyData, setLoadingCompanyData] = useState(false);
  const [companyListLoadState, setCompanyListLoadState] = useState<CompanyListLoadState>('idle');
  const [profileLoadStatus, setProfileLoadStatus] = useState<CompanyProfileStatus>(emptyCompanyProfileStatus);
  const [companyResourceStatus, setCompanyResourceStatus] = useState<Record<CompanyResourceKey, CompanyResourceStatus>>(
    emptyCompanyResourceStatus()
  );
  const [savingCompanyRegistry, setSavingCompanyRegistry] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [archivingCompany, setArchivingCompany] = useState(false);
  const [restoringCompany, setRestoringCompany] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(false);
  const [busyLocationId, setBusyLocationId] = useState<number | null>(null);
  const [busyGroupId, setBusyGroupId] = useState<number | null>(null);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [resettingUserId, setResettingUserId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedRecord = selectedCompanyId === null
    ? null
    : records.find((company) => company.id === selectedCompanyId) ?? null;
  const selectedProfile = profile && profile.id === selectedCompanyId ? profile : null;
  const placeholderTabs = selectedRecord ? buildPlaceholderTabs(selectedRecord) : [];
  const administratorCount = users.filter((record) =>
    record.company_roles.some((role) => role.role_key === 'company_admin') ||
    record.group_memberships.some((membership) =>
      membership.roles.some((role) => role.role_key === 'scheduler_admin')
    )
  ).length;
  const showTrueEmptyCompanyState = companyListLoadState === 'loaded' && records.length === 0;
  const companyListLoadFailed = companyListLoadState === 'failed';
  const failedCompanyResources = (Object.keys(companyResourceStatus) as CompanyResourceKey[]).filter(
    (key) => companyResourceStatus[key].error !== null
  );
  const hasInitialCompanyResourceFailure = failedCompanyResources.some((key) => !companyResourceStatus[key].hasLoadedOnce);
  const companyResourceAlert =
    failedCompanyResources.length === 0
      ? null
      : failedCompanyResources.length === 1
        ? `${companyResourceLabels[failedCompanyResources[0]]} could not be loaded.`
        : `${failedCompanyResources.map((key) => companyResourceLabels[key]).join(', ')} could not be loaded.`;

  const resetCompanyResourceData = () => {
    setLocations([]);
    setGroups([]);
    setUsers([]);
    setUsersSummary(null);
    setPendingInvites([]);
    setUserDraft(emptyUserFormState([]));
    setCompanyResourceStatus(emptyCompanyResourceStatus());
  };

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    setError(null);

    try {
      const payload = await fetchCompanies();
      setRecords(payload.data.data);
      setSummary(payload.summary);
      setSelectedCompanyId((current) => {
        if (payload.data.data.length === 0) {
          return null;
        }

        if (current !== null && payload.data.data.some((record) => record.id === current)) {
          return current;
        }

        return payload.data.data[0].id;
      });
      setCompanyListLoadState('loaded');
    } catch (requestError) {
      setCompanyListLoadState('failed');
      setError(requestError instanceof Error ? requestError.message : 'Unable to load clients.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadCompanyProfileData = async (companyId: number) => {
    const hadLoadedProfile = profile?.id === companyId;

    setLoadingProfile(true);
    setProfileLoadStatus((current) => ({
      ...current,
      state: 'loading',
      error: null,
    }));

    try {
      const nextProfile = await fetchCompanyProfile(companyId);
      setProfile(nextProfile);
      setFormState(profileToFormState(nextProfile));
      setBrandingDraft(emptyBrandingDraftState);
      setFieldErrors({});
      setProfileLoadStatus({
        state: 'loaded',
        error: null,
        hasLoadedOnce: true,
      });
    } catch (requestError) {
      setProfileLoadStatus((current) => ({
        ...current,
        state: 'failed',
        error: requestError instanceof Error ? requestError.message : 'Unable to load company profile.',
      }));

      if (!hadLoadedProfile) {
        setProfile(null);
        setFormState(emptyProfileFormState);
        setBrandingDraft(emptyBrandingDraftState);
        setFieldErrors({});
      }
    } finally {
      setLoadingProfile(false);
    }
  };

  const retryCompanyProfileLoad = () => {
    if (selectedCompanyId === null) {
      return;
    }

    void loadCompanyProfileData(selectedCompanyId);
  };

  const loadCompanyResourceData = async (
    companyId: number,
    options?: {
      preserveExistingData?: boolean;
      resourceKeys?: CompanyResourceKey[];
    }
  ) => {
    const preserveExistingData = options?.preserveExistingData ?? false;
    const resourceKeys = options?.resourceKeys ?? ['locations', 'groups', 'users'];

    setLoadingCompanyData(true);
    setCompanyResourceStatus((current) => {
      const nextState = { ...current };

      resourceKeys.forEach((key) => {
        nextState[key] = {
          ...current[key],
          state: 'loading',
          error: null,
        };
      });

      return nextState;
    });

    if (!preserveExistingData) {
      setLocations([]);
      setGroups([]);
      setUsers([]);
      setUsersSummary(null);
      setPendingInvites([]);
      setUserDraft(emptyUserFormState([]));
    }

    const requestFactories: Record<CompanyResourceKey, () => Promise<unknown>> = {
      locations: () => fetchCompanyLocations(companyId),
      groups: () => fetchCompanyGroups(companyId),
      users: () => fetchCompanyUsers(companyId),
    };

    const results = await Promise.allSettled(resourceKeys.map((key) => requestFactories[key]()));

    results.forEach((result, index) => {
      const resourceKey = resourceKeys[index];
      const resourceLabel = companyResourceLabels[resourceKey];

      if (result.status === 'fulfilled') {
        if (resourceKey === 'locations') {
          setLocations(sortLocations(result.value as CompanyLocationRecord[]));
        }

        if (resourceKey === 'groups') {
          const sortedGroupRecords = sortGroups(result.value as CompanyGroupRecord[]);
          setGroups(sortedGroupRecords);
          setUserDraft(emptyUserFormState(sortedGroupRecords));
        }

        if (resourceKey === 'users') {
          const nextUsers = result.value as CompanyUsersResponse;
          setUsers(sortUsers(nextUsers.data));
          setUsersSummary(nextUsers.summary);
          setPendingInvites(nextUsers.pending_invites);
        }

        setCompanyResourceStatus((current) => ({
          ...current,
          [resourceKey]: {
            state: 'loaded',
            error: null,
            hasLoadedOnce: true,
          },
        }));
        return;
      }

      setCompanyResourceStatus((current) => ({
        ...current,
        [resourceKey]: {
          ...current[resourceKey],
          state: 'failed',
          error: formatResourceLoadError(resourceLabel, result.reason),
        },
      }));
    });

    setLoadingCompanyData(false);
  };

  const retryFailedCompanyResources = () => {
    if (selectedCompanyId === null || failedCompanyResources.length === 0) {
      return;
    }

    void loadCompanyResourceData(selectedCompanyId, {
      preserveExistingData: true,
      resourceKeys: failedCompanyResources,
    });
  };

  useEffect(() => {
    void loadCompanies();
  }, []);

  useEffect(() => {
    if (records.length === 0) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const requestedCompanySlug = params.get('company');
    const requestedTab = params.get('tab');
    const requestedCompany = requestedCompanySlug
      ? records.find((record) => record.slug === requestedCompanySlug) ?? null
      : null;
    const nextTab = requestedTab ? companyQueryTabMap[requestedTab] : undefined;

    if (requestedCompany) {
      setSelectedCompanyId((current) => current === requestedCompany.id ? current : requestedCompany.id);
    }

    if (nextTab) {
      setActiveTab((current) => current === nextTab ? current : nextTab);
    }
  }, [location.search, records]);

  const selectCompany = (companyId: number | null) => {
    setSelectedCompanyId(companyId);

    const params = new URLSearchParams(location.search);
    const company = companyId === null ? null : records.find((record) => record.id === companyId) ?? null;
    if (company) {
      params.set('company', company.slug);
    } else {
      params.delete('company');
    }

    navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
  };

  const archiveSelectedCompany = async () => {
    if (!selectedRecord || selectedRecord.status === 'archived') {
      return;
    }

    setArchivingCompany(true);
    setError(null);
    setSuccess(null);

    try {
      const archived = await archiveCompany(selectedRecord.id);
      setRecords((current) => current.map((record) => record.id === archived.id ? archived : record));
      setSuccess(`${archived.name} was archived.`);
      setShowArchiveModal(false);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to archive this client.');
    } finally {
      setArchivingCompany(false);
    }
  };

  const restoreSelectedCompany = async () => {
    if (!selectedRecord || selectedRecord.status !== 'archived') {
      return;
    }

    setRestoringCompany(true);
    setError(null);
    setSuccess(null);
    try {
      const restored = await restoreCompany(selectedRecord.id);
      setRecords((current) => current.map((record) => record.id === restored.id ? restored : record));
      setSuccess(`${restored.name} was restored as ${statusLabelMap[restored.status]}.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to restore this client.');
    } finally {
      setRestoringCompany(false);
    }
  };

  const deleteSelectedCompany = async () => {
    if (!selectedRecord || !selectedRecord.can_delete) {
      return;
    }

    setDeletingCompany(true);
    setError(null);
    setSuccess(null);
    try {
      await permanentlyDeleteCompany(selectedRecord.id, {
        confirmation_name: deleteCompanyName,
        reason: deleteCompanyReason.trim(),
        confirm_permanent_delete: deleteCompanyAcknowledged,
      });
      const deletedName = selectedRecord.name;
      setShowDeleteCompanyModal(false);
      setDeleteCompanyName('');
      setDeleteCompanyReason('');
      setDeleteCompanyAcknowledged(false);
      setSelectedCompanyId(null);
      await loadCompanies();
      setSuccess(`${deletedName} was permanently deleted.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to permanently delete this client.');
    } finally {
      setDeletingCompany(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId === null) {
      setProfile(null);
      setFormState(emptyProfileFormState);
      setBrandingDraft(emptyBrandingDraftState);
      setFieldErrors({});
      setProfileLoadStatus(emptyCompanyProfileStatus());
      resetCompanyResourceData();
      return;
    }

    setSuccess(null);
    resetCompanyResourceData();
    void loadCompanyProfileData(selectedCompanyId);
    void loadCompanyResourceData(selectedCompanyId);
  }, [selectedCompanyId]);

  const handleFieldChange = (field: keyof CompanyProfileFormState, value: string) => {
    setFormState((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleBrandingFileChange = (field: 'logoFile' | 'logoMarkFile', file: File | null) => {
    setBrandingDraft((current) => ({
      ...current,
      [field]: file,
      ...(field === 'logoFile' && file !== null ? { removeLogo: false } : {}),
      ...(field === 'logoMarkFile' && file !== null ? { logoMarkUrl: '', removeLogoMark: false } : {}),
    }));
  };

  const handleBrandingUrlChange = (value: string) => {
    setBrandingDraft((current) => ({
      ...current,
      logoMarkUrl: value,
      ...(value.trim() !== '' ? { logoMarkFile: null, removeLogoMark: false } : {}),
    }));
  };

  const handleBrandingRemoveChange = (field: 'removeLogo' | 'removeLogoMark', value: boolean) => {
    setBrandingDraft((current) => ({
      ...current,
      [field]: value,
      ...(field === 'removeLogo' && value ? { logoFile: null } : {}),
      ...(field === 'removeLogoMark' && value ? { logoMarkFile: null, logoMarkUrl: '' } : {}),
    }));
  };

  const handleCompanyRegistryFieldChange = (field: keyof CompanyRegistryFormState, value: string) => {
    setCompanyRegistryDraft((current) => ({ ...current, [field]: value }));
    setCompanyRegistryFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleLocationFieldChange = (field: keyof CompanyLocationFormState, value: string | boolean) => {
    setLocationDraft((current) => ({ ...current, [field]: value }));
    setLocationFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleGroupFieldChange = (field: keyof CompanyGroupFormState, value: string | number | null) => {
    setGroupDraft((current) => ({ ...current, [field]: value }));
    setGroupFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleUserFieldChange = (field: keyof Omit<CompanyUserFormState, 'group_access'>, value: string | number | null) => {
    setUserDraft((current) => {
      if (field !== 'access_level') {
        return { ...current, [field]: value as never };
      }

      const accessLevel = value as UserAccessLevel;
      return {
        ...current,
        access_level: accessLevel,
        group_access: accessLevelIncludesSchedulerAdmin(accessLevel)
          ? current.group_access
          : current.group_access.map((entry) =>
              entry.role_key === 'scheduler_admin' ? { ...entry, role_key: 'member' } : entry
            ),
      };
    });
    setUserFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleGroupAccessRoleChange = (groupId: number, roleKey: GroupAccessRole) => {
    setUserDraft((current) => ({
      ...current,
      group_access: current.group_access.map((entry) =>
        entry.group_id === groupId ? { ...entry, role_key: roleKey } : entry
      ),
    }));
    setUserFieldErrors((current) => ({ ...current, group_memberships: undefined }));
  };

  const closeLocationModal = () => {
    setShowLocationModal(false);
    setLocationDraft(emptyLocationFormState);
    setLocationFieldErrors({});
  };

  const closeCompanyRegistryModal = () => {
    setShowCompanyRegistryModal(false);
    setCompanyRegistryDraft(emptyCompanyRegistryFormState);
    setCompanyRegistryFieldErrors({});
  };

  const closeGroupModal = () => {
    setShowGroupModal(false);
    setGroupDraft(emptyGroupFormState);
    setGroupFieldErrors({});
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setUserDraft(emptyUserFormState(groups));
    setUserFieldErrors({});
  };

  const openCreateLocationModal = () => {
    setLocationDraft(emptyLocationFormState);
    setLocationFieldErrors({});
    setShowLocationModal(true);
  };

  const openCreateCompanyRegistryModal = () => {
    setCompanyRegistryDraft(emptyCompanyRegistryFormState);
    setCompanyRegistryFieldErrors({});
    setShowCompanyRegistryModal(true);
  };

  const openEditLocationModal = (record: CompanyLocationRecord) => {
    setLocationDraft(locationToFormState(record));
    setLocationFieldErrors({});
    setShowLocationModal(true);
  };

  const openCreateGroupModal = (kind: 'department' | 'team') => {
    setGroupDraft({ ...emptyGroupFormState, kind });
    setGroupFieldErrors({});
    setShowGroupModal(true);
  };

  const openEditGroupModal = (record: CompanyGroupRecord) => {
    setGroupDraft(groupToFormState(record));
    setGroupFieldErrors({});
    setShowGroupModal(true);
  };

  const openCreateUserModal = () => {
    setUserDraft(emptyUserFormState(groups));
    setUserFieldErrors({});
    setShowUserModal(true);
  };

  const openEditUserModal = (record: CompanyUserRecord) => {
    setUserDraft(userToFormState(record, groups));
    setUserFieldErrors({});
    setShowUserModal(true);
  };

  const openPasswordModal = (record: CompanyUserRecord) => {
    setPasswordDraft({
      membershipId: record.id,
      name: record.user?.name ?? 'Pending user',
      email: record.user?.email ?? '',
      password: '',
      password_confirmation: '',
    });
    setPasswordFieldErrors({});
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordFieldErrors({});
    setPasswordDraft(emptyPasswordDraftState);
  };

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedCompanyId === null) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    try {
      const payload = new FormData();
      payload.append('_method', 'PATCH');
      Object.entries(formState).forEach(([key, value]) => {
        payload.append(key, value.trim());
      });
      if (brandingDraft.logoFile) {
        payload.append('logo', brandingDraft.logoFile);
      }
      if (brandingDraft.logoMarkFile) {
        payload.append('logo_mark', brandingDraft.logoMarkFile);
      }
      if (brandingDraft.logoMarkUrl.trim()) {
        payload.append('logo_mark_url', brandingDraft.logoMarkUrl.trim());
      }
      if (brandingDraft.removeLogo) {
        payload.append('remove_logo', '1');
      }
      if (brandingDraft.removeLogoMark) {
        payload.append('remove_logo_mark', '1');
      }
      const nextProfile = await updateCompanyProfile(selectedCompanyId, payload);

      setProfile(nextProfile);
      setFormState(profileToFormState(nextProfile));
      setBrandingDraft(emptyBrandingDraftState);
      setSuccess('Company info saved.');
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Unable to save company profile.';
      const validation =
        requestError instanceof Error
          ? ((requestError as Error & { validation?: { errors?: Record<string, string[]> } }).validation?.errors ?? {})
          : {};

      setFieldErrors(extractValidationErrors(validation, Object.keys(emptyProfileFormState) as Array<keyof CompanyProfileFormState>));
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCompanyRegistrySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSavingCompanyRegistry(true);
    setCompanyRegistryFieldErrors({});
    setError(null);
    setSuccess(null);

    try {
      const record = await createCompany({
        name: companyRegistryDraft.name.trim(),
        slug: companyRegistryDraft.slug.trim(),
      });

      setSelectedCompanyId(record.id);
      await loadCompanies();
      closeCompanyRegistryModal();
      setSuccess(`Created ${record.name}. Continue with company info, users, and department assignments below.`);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Unable to create company.';
      const validation =
        requestError instanceof Error
          ? ((requestError as Error & { validation?: { errors?: Record<string, string[]> } }).validation?.errors ?? {})
          : {};

      setCompanyRegistryFieldErrors(
        extractValidationErrors(validation, Object.keys(emptyCompanyRegistryFormState) as Array<keyof CompanyRegistryFormState>)
      );
      setError(message);
    } finally {
      setSavingCompanyRegistry(false);
    }
  };

  const handleLocationSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedCompanyId === null) {
      return;
    }

    setSavingLocation(true);
    setError(null);
    setSuccess(null);
    setLocationFieldErrors({});

    try {
      const payload = {
        code: locationDraft.code.trim(),
        display_name: locationDraft.display_name.trim(),
        public_label: locationDraft.public_label.trim(),
        location_group: locationDraft.location_group.trim(),
        timezone: locationDraft.timezone.trim(),
        is_active: locationDraft.is_active,
        notes: locationDraft.notes.trim(),
      };

      const record = locationDraft.id === null
        ? await createCompanyLocation(selectedCompanyId, payload)
        : await updateCompanyLocation(selectedCompanyId, locationDraft.id, payload);

      setLocations((current) =>
        sortLocations([
          ...current.filter((candidate) => candidate.id !== record.id),
          record,
        ])
      );
      await loadCompanies();
      closeLocationModal();
      setSuccess(locationDraft.id === null ? 'Location created.' : 'Location updated.');
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Unable to save location.';
      const validation =
        requestError instanceof Error
          ? ((requestError as Error & { validation?: { errors?: Record<string, string[]> } }).validation?.errors ?? {})
          : {};

      setLocationFieldErrors(
        extractValidationErrors(validation, Object.keys(emptyLocationFormState) as Array<keyof CompanyLocationFormState>)
      );
      setError(message);
    } finally {
      setSavingLocation(false);
    }
  };

  const handleGroupSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedCompanyId === null) {
      return;
    }

    setSavingGroup(true);
    setError(null);
    setSuccess(null);
    setGroupFieldErrors({});

    try {
      const payload = {
        parent_group_id: groupDraft.parent_group_id,
        name: groupDraft.name.trim(),
        slug: groupDraft.slug.trim(),
        kind: groupDraft.kind.trim(),
        status: groupDraft.status,
      };

      const record = groupDraft.id === null
        ? await createCompanyGroup(selectedCompanyId, payload)
        : await updateCompanyGroup(selectedCompanyId, groupDraft.id, payload);

      setGroups((current) =>
        sortGroups([
          ...current.filter((candidate) => candidate.id !== record.id),
          record,
        ])
      );
      await loadCompanies();
      closeGroupModal();
      setSuccess(groupDraft.id === null ? 'Department created.' : 'Department updated.');
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Unable to save department.';
      const validation =
        requestError instanceof Error
          ? ((requestError as Error & { validation?: { errors?: Record<string, string[]> } }).validation?.errors ?? {})
          : {};

      setGroupFieldErrors(extractValidationErrors(validation, Object.keys(emptyGroupFormState) as Array<keyof CompanyGroupFormState>));
      setError(message);
    } finally {
      setSavingGroup(false);
    }
  };

  const handleLocationDelete = async (record: CompanyLocationRecord) => {
    if (selectedCompanyId === null) {
      return;
    }

    if (!window.confirm(`Delete location "${record.display_name}"?`)) {
      return;
    }

    setBusyLocationId(record.id);
    setError(null);
    setSuccess(null);

    try {
      await deleteCompanyLocation(selectedCompanyId, record.id);
      setLocations((current) => current.filter((candidate) => candidate.id !== record.id));
      await loadCompanies();
      setSuccess('Location deleted.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to delete location.');
    } finally {
      setBusyLocationId(null);
    }
  };

  const handleGroupDelete = async (record: CompanyGroupRecord) => {
    if (selectedCompanyId === null) {
      return;
    }

    if (!window.confirm(`Delete department "${record.name}"?`)) {
      return;
    }

    setBusyGroupId(record.id);
    setError(null);
    setSuccess(null);

    try {
      await deleteCompanyGroup(selectedCompanyId, record.id);
      setGroups((current) => current.filter((candidate) => candidate.id !== record.id));
      await loadCompanies();
      setSuccess('Department deleted.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to delete department.');
    } finally {
      setBusyGroupId(null);
    }
  };

  const handleUserSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedCompanyId === null) {
      return;
    }

    setSavingUser(true);
    setError(null);
    setSuccess(null);
    setUserFieldErrors({});

    try {
      const payload = {
        email: userDraft.email.trim(),
        name: userDraft.name.trim(),
        status: userDraft.status,
        company_role_keys: userDraft.access_level === 'company_admin' || userDraft.access_level === 'company_admin_group_schedule_admin'
          ? ['member', 'company_admin']
          : ['member'],
        group_memberships: userDraft.group_access
          .filter((entry) => entry.role_key)
          .map((entry) => ({
            group_id: entry.group_id,
            status: userDraft.status,
            role_keys: [entry.role_key],
          })),
      };

      const record = userDraft.id === null
        ? (await createCompanyUser(selectedCompanyId, payload)).record
        : await updateCompanyUser(selectedCompanyId, userDraft.id, payload);

      setUsers((current) =>
        sortUsers([
          ...current.filter((candidate) => candidate.id !== record.id),
          record,
        ])
      );
      await loadCompanies();
      await loadCompanyResourceData(selectedCompanyId, { preserveExistingData: true });
      closeUserModal();
      setSuccess(userDraft.id === null ? 'User access created.' : 'User access updated.');
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Unable to save company user access.';
      const validation =
        requestError instanceof Error
          ? ((requestError as Error & { validation?: { errors?: Record<string, string[]> } }).validation?.errors ?? {})
          : {};

      setUserFieldErrors({
        email: validation.email?.[0],
        name: validation.name?.[0],
        status: validation.status?.[0],
        company_role_keys: validation.company_role_keys?.[0],
        group_memberships: validation.group_memberships?.[0] ?? validation['group_memberships.0.group_id']?.[0],
      });
      setError(message);
    } finally {
      setSavingUser(false);
    }
  };

  const handlePasswordFieldChange = (field: 'password' | 'password_confirmation', value: string) => {
    setPasswordDraft((current) => ({ ...current, [field]: value }));
    setPasswordFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleUserPasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedCompanyId === null || passwordDraft.membershipId === null) {
      return;
    }

    setSavingPassword(true);
    setResettingUserId(passwordDraft.membershipId);
    setError(null);
    setSuccess(null);
    setPasswordFieldErrors({});

    try {
      const payload = await updateCompanyUserPassword(selectedCompanyId, passwordDraft.membershipId, {
        password: passwordDraft.password,
        password_confirmation: passwordDraft.password_confirmation,
      });

      closePasswordModal();
      setSuccess(`Password updated for ${payload.email}.`);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Unable to update password.';
      const validation =
        requestError instanceof Error
          ? ((requestError as Error & { validation?: { errors?: Record<string, string[]> } }).validation?.errors ?? {})
          : {};

      setPasswordFieldErrors({
        password: validation.password?.[0],
        password_confirmation: validation.password_confirmation?.[0],
      });
      setError(message);
    } finally {
      setSavingPassword(false);
      setResettingUserId(null);
    }
  };

  const handleUserDelete = async (record: CompanyUserRecord) => {
    if (selectedCompanyId === null) {
      return;
    }

    if (!window.confirm(`Remove ${record.user?.email ?? 'this user'} from the selected company?`)) {
      return;
    }

    setBusyUserId(record.id);
    setError(null);
    setSuccess(null);

    try {
      await deleteCompanyUser(selectedCompanyId, record.id);
      setUsers((current) => current.filter((candidate) => candidate.id !== record.id));
      await loadCompanies();
      await loadCompanyResourceData(selectedCompanyId, { preserveExistingData: true });
      setSuccess('User removed from company.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to remove company user.');
    } finally {
      setBusyUserId(null);
    }
  };

  const showLoadingState = loadingCompanies || loadingProfile || loadingCompanyData;

  return (
    <Fragment>
      <CaresPageHeader
        homepage="CARES Administration"
        activepage="Admin"
        page="Clients"
      />

      <Card className="custom-card mb-3">
        <Card.Body className="p-3">
          <div className="d-flex flex-column flex-lg-row align-items-lg-end justify-content-between gap-3">
            <div className="flex-grow-1" style={{ maxWidth: '32rem' }}>
              <p className="text-uppercase text-muted fs-11 fw-semibold mb-1">Client workspace</p>
              <div className="d-flex flex-column flex-sm-row gap-2">
                <Form.Select
                  id="selected-client-dropdown"
                  aria-label="Select client workspace"
                  value={selectedCompanyId ?? ''}
                  disabled={loadingCompanies || records.length === 0}
                  onChange={(event) => selectCompany(event.target.value ? Number(event.target.value) : null)}
                >
                  <option value="">{companyListLoadFailed ? 'Unable to load clients' : loadingCompanies ? 'Loading clients…' : 'Select client…'}</option>
                  {records.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                </Form.Select>
            <Button variant="secondary-light" className="btn-wave" onClick={openCreateCompanyRegistryModal}>
              <i className="ri-add-circle-line align-middle me-1"></i>
              Provision new client
            </Button>
              </div>
            </div>
            <div className="text-lg-end">
              <p className="text-uppercase text-muted fs-11 fw-semibold mb-1">Current client</p>
              <strong>{selectedRecord?.name ?? 'No client selected'}</strong>
              <div className="text-muted fs-12">Choose a client, then use the subsection menu below.</div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col xl={12}>
          <Card className="custom-card overflow-hidden">
            <Card.Body className="p-3">
              <div className="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3">
                <div className="d-flex align-items-start gap-3 flex-grow-1">
                  {selectedRecord ? (
                    selectedProfile?.branding.logo_mark_url || selectedProfile?.branding.logo_url ? (
                      <div
                        className="rounded border bg-white d-flex align-items-center justify-content-center p-2 flex-shrink-0"
                        style={{ width: '112px', height: '72px' }}
                      >
                        <img
                          src={selectedProfile.branding.logo_mark_url || selectedProfile.branding.logo_url || ''}
                          alt={`${selectedRecord.name} logo`}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      </div>
                    ) : (
                      <div className="avatar avatar-xxl avatar-rounded bg-primary-transparent text-primary fs-24 fw-semibold">
                        {toInitials(selectedRecord.name)}
                      </div>
                    )
                  ) : null}
                  <div className="flex-grow-1 align-self-center">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <h3 className="mb-0">{selectedRecord?.name ?? 'No client selected'}</h3>
                      {selectedRecord ? (
                        <Badge bg={companyStatusToneMap[selectedRecord.status]} className="badge-md">
                          {statusLabelMap[selectedRecord.status]}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="row row-cols-2 row-cols-sm-4 g-2 w-100 flex-shrink-0" style={{ maxWidth: '35rem' }}>
                  {[
                    ['Administrators', administratorCount],
                    ['Departments / Groups', selectedRecord?.counts.groups_total ?? 0],
                    ['Locations', selectedRecord?.counts.locations_total ?? 0],
                    ['Users', selectedRecord?.counts.memberships_total ?? 0],
                  ].map(([label, value]) => (
                    <div className="col" key={String(label)}>
                      <div className="rounded border p-2 text-center h-100">
                        <div className="fw-semibold fs-18">{value}</div>
                        <div className="text-muted fs-11">{label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <Card className="custom-card">
            <Card.Body className="p-0">
              {showLoadingState ? (
                <div className="p-4 d-flex align-items-center gap-3 text-muted">
                  <Spinner animation="border" size="sm" />
                  <span>
                    {loadingCompanies
                      ? 'Loading clients...'
                      : loadingProfile
                        ? 'Loading company profile...'
                        : 'Loading company resources...'}
                  </span>
                </div>
              ) : null}
              {error ? (
                <div className="p-4 pb-0">
                  <Alert variant="danger" className="mb-0 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                    <span>{error}</span>
                    {companyListLoadFailed ? (
                      <Button variant="danger" className="btn-wave" onClick={() => void loadCompanies()}>
                        Retry loading clients
                      </Button>
                    ) : null}
                  </Alert>
                </div>
              ) : null}
              {companyResourceAlert ? (
                <div className="p-4 pb-0">
                  <Alert
                    variant={hasInitialCompanyResourceFailure ? 'danger' : 'warning'}
                    className="mb-0 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
                  >
                    <span>{companyResourceAlert}</span>
                    <Button
                      variant={hasInitialCompanyResourceFailure ? 'danger' : 'warning'}
                      className="btn-wave"
                      onClick={retryFailedCompanyResources}
                    >
                      Retry failed resources
                    </Button>
                  </Alert>
                </div>
              ) : null}
              {success ? (
                <div className="p-4 pb-0">
                  <Alert variant="success" className="mb-0">
                    {success}
                  </Alert>
                </div>
              ) : null}
              {!loadingCompanies && showTrueEmptyCompanyState ? (
                <div className="p-4">
                  <Alert variant="warning" className="mb-0 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                    <span>No clients are available yet. Provision the first client here to unlock tenant info, users, and group-scoped scheduling access.</span>
                    <Button variant="primary" className="btn-wave" onClick={openCreateCompanyRegistryModal}>
                      Create first client
                    </Button>
                  </Alert>
                </div>
              ) : null}
              <Tab.Container
                activeKey={activeTab}
                onSelect={(eventKey) => {
                  if (eventKey) {
                    setActiveTab(eventKey as CompanyTabKey);
                  }
                }}
              >
                <div className="p-3 border-bottom border-block-end-dashed">
                  <Nav as="ul" variant="pills" className="mb-0 tab-style-6 gap-2 flex-wrap">
                    {companyTabOrder.map((tabKey) => (
                      <Nav.Item as="li" key={tabKey}>
                        <Nav.Link eventKey={tabKey}>{companyTabLabels[tabKey]}</Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                </div>
                <div className="p-4">
                  <Tab.Content>
                    <Tab.Pane eventKey="company-info">
                      {selectedCompanyId === null ? (
                        <div className="text-muted">Select a client to load its profile.</div>
                      ) : (
                        <>
                          <ProfileLoadStateNotice status={profileLoadStatus} onRetry={retryCompanyProfileLoad} />
                          {selectedProfile ? (
                            <CompanyInfoTab
                              profile={selectedProfile}
                              formState={formState}
                              brandingDraft={brandingDraft}
                              fieldErrors={fieldErrors}
                              saving={saving}
                              onChange={handleFieldChange}
                              onBrandingFileChange={handleBrandingFileChange}
                              onBrandingUrlChange={handleBrandingUrlChange}
                              onBrandingRemoveChange={handleBrandingRemoveChange}
                              onSubmit={handleSave}
                            />
                          ) : profileLoadStatus.state === 'failed' ? null : (
                            <div className="text-muted">Loading the selected client profile.</div>
                          )}
                        </>
                      )}
                    </Tab.Pane>
                    <Tab.Pane eventKey="locations">
                      <ResourceLoadStateNotice
                        label="Locations"
                        status={companyResourceStatus.locations}
                        onRetry={() => {
                          if (selectedCompanyId !== null) {
                            void loadCompanyResourceData(selectedCompanyId, {
                              preserveExistingData: true,
                              resourceKeys: ['locations'],
                            });
                          }
                        }}
                      />
                      {companyResourceStatus.locations.state === 'failed' && !companyResourceStatus.locations.hasLoadedOnce ? null : (
                        <>
                          <LocationsTab
                            locations={locations}
                            busyRecordId={busyLocationId}
                            onCreate={openCreateLocationModal}
                            onEdit={openEditLocationModal}
                            onDelete={handleLocationDelete}
                          />
                          <CompanyResourceBulkSetup
                            resource="locations"
                            tenantQuery=""
                            locations={locations}
                            groups={groups}
                            createLocation={(payload) => {
                              if (selectedCompanyId === null) throw new Error('Select a client before importing locations.');
                              return createCompanyLocation(selectedCompanyId, payload);
                            }}
                            onLocationsImported={(records) => {
                              setLocations((current) => sortLocations([...current, ...records]));
                              setSuccess(`${records.length} locations imported.`);
                              void loadCompanies();
                            }}
                            onGroupsImported={() => undefined}
                          />
                        </>
                      )}
                    </Tab.Pane>
                    <Tab.Pane eventKey="teams-groups">
                      <ResourceLoadStateNotice
                        label="Departments & Groups"
                        status={companyResourceStatus.groups}
                        onRetry={() => {
                          if (selectedCompanyId !== null) {
                            void loadCompanyResourceData(selectedCompanyId, {
                              preserveExistingData: true,
                              resourceKeys: ['groups'],
                            });
                          }
                        }}
                      />
                      {companyResourceStatus.groups.state === 'failed' && !companyResourceStatus.groups.hasLoadedOnce ? null : (
                        <>
                          <TeamsGroupsTab
                            groups={groups}
                            busyRecordId={busyGroupId}
                            onCreate={openCreateGroupModal}
                            onEdit={openEditGroupModal}
                            onDelete={handleGroupDelete}
                          />
                          <CompanyResourceBulkSetup
                            resource="groups"
                            tenantQuery=""
                            locations={locations}
                            groups={groups}
                            createGroup={(payload) => {
                              if (selectedCompanyId === null) throw new Error('Select a client before importing Departments and Groups.');
                              return createCompanyGroup(selectedCompanyId, payload);
                            }}
                            onLocationsImported={() => undefined}
                            onGroupsImported={(records) => {
                              setGroups((current) => sortGroups([...current, ...records]));
                              setSuccess(`${records.length} Departments / Groups imported.`);
                              void loadCompanies();
                            }}
                          />
                        </>
                      )}
                    </Tab.Pane>
                    <Tab.Pane eventKey="users">
                      <ResourceLoadStateNotice
                        label="Users"
                        status={companyResourceStatus.users}
                        onRetry={() => {
                          if (selectedCompanyId !== null) {
                            void loadCompanyResourceData(selectedCompanyId, {
                              preserveExistingData: true,
                              resourceKeys: ['users'],
                            });
                          }
                        }}
                      />
                      {companyResourceStatus.users.state === 'failed' && !companyResourceStatus.users.hasLoadedOnce ? null : (
                        <UsersTab
                          usersSummary={usersSummary}
                          users={users}
                          pendingInvites={pendingInvites}
                          busyRecordId={busyUserId}
                          resettingRecordId={resettingUserId}
                          onCreate={openCreateUserModal}
                          onEdit={openEditUserModal}
                          onPasswordUpdate={openPasswordModal}
                          onDelete={handleUserDelete}
                        />
                      )}
                    </Tab.Pane>
                    <Tab.Pane eventKey="roles-permissions">
                      <ResourceLoadStateNotice
                        label="Users"
                        status={companyResourceStatus.users}
                        onRetry={() => {
                          if (selectedCompanyId !== null) {
                            void loadCompanyResourceData(selectedCompanyId, {
                              preserveExistingData: true,
                              resourceKeys: ['users'],
                            });
                          }
                        }}
                      />
                      {companyResourceStatus.users.state === 'failed' && !companyResourceStatus.users.hasLoadedOnce ? null : (
                        <RolesPermissionsTab users={users} />
                      )}
                    </Tab.Pane>
                    {placeholderTabs.map((tab) => (
                      <Tab.Pane eventKey={tab.key} key={tab.key}>
                        <PlaceholderTab tab={tab} />
                      </Tab.Pane>
                    ))}
                  </Tab.Content>
                </div>
              </Tab.Container>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {selectedRecord ? (
        <Card className="custom-card border-danger mt-3">
          <Card.Body className="p-3 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
            <div>
              <h6 className="mb-1">Client lifecycle and retention</h6>
              <p className="text-muted fs-12 mb-0">
                Archive preserves history and can be reversed. Permanent deletion is limited to empty Draft clients created by mistake.
              </p>
              {!selectedRecord.can_delete && selectedRecord.delete_blockers.length > 0 ? (
                <div className="text-muted fs-11 mt-1">Delete unavailable: {selectedRecord.delete_blockers[0]}</div>
              ) : null}
            </div>
            <div className="d-flex flex-wrap gap-2 flex-shrink-0">
              {selectedRecord.status === 'archived' ? (
                <Button variant="success-light" className="btn-wave" disabled={restoringCompany} onClick={() => void restoreSelectedCompany()}>
                  {restoringCompany ? 'Restoring…' : 'Restore client'}
                </Button>
              ) : (
                <Button variant="danger-light" className="btn-wave" onClick={() => setShowArchiveModal(true)}>
                  Archive client
                </Button>
              )}
              <Button
                variant="danger"
                className="btn-wave"
                disabled={!selectedRecord.can_delete}
                onClick={() => setShowDeleteCompanyModal(true)}
              >
                Delete permanently
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      <Modal show={showArchiveModal} onHide={() => !archivingCompany && setShowArchiveModal(false)} centered>
        <Modal.Header closeButton={!archivingCompany}>
          <Modal.Title>Archive {selectedRecord?.name ?? 'client'}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-0">
            This removes the client from active operations. Historical records remain available for audit and recovery.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" disabled={archivingCompany} onClick={() => setShowArchiveModal(false)}>No, keep client</Button>
          <Button variant="danger" disabled={archivingCompany} onClick={() => void archiveSelectedCompany()}>
            {archivingCompany ? 'Archiving…' : 'Yes, archive client'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteCompanyModal}
        onHide={() => !deletingCompany && setShowDeleteCompanyModal(false)}
        centered
      >
        <Modal.Header closeButton={!deletingCompany}>
          <Modal.Title>Delete {selectedRecord?.name ?? 'client'} permanently?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            This cannot be undone. Use Archive for any client with operational or historical data.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>Type the exact client name</Form.Label>
            <Form.Control
              value={deleteCompanyName}
              onChange={(event) => setDeleteCompanyName(event.target.value)}
              placeholder={selectedRecord?.name ?? ''}
              autoComplete="off"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Reason for permanent deletion</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={deleteCompanyReason}
              onChange={(event) => setDeleteCompanyReason(event.target.value)}
              placeholder="Explain why this record was created in error."
            />
          </Form.Group>
          <Form.Check
            checked={deleteCompanyAcknowledged}
            onChange={(event) => setDeleteCompanyAcknowledged(event.target.checked)}
            label="I understand this permanently deletes the client record."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" disabled={deletingCompany} onClick={() => setShowDeleteCompanyModal(false)}>Cancel</Button>
          <Button
            variant="danger"
            disabled={
              deletingCompany ||
              deleteCompanyName !== (selectedRecord?.name ?? '') ||
              deleteCompanyReason.trim().length < 10 ||
              !deleteCompanyAcknowledged
            }
            onClick={() => void deleteSelectedCompany()}
          >
            {deletingCompany ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </Modal.Footer>
      </Modal>

      <CompanyRegistryModal
        draft={companyRegistryDraft}
        fieldErrors={companyRegistryFieldErrors}
        saving={savingCompanyRegistry}
        show={showCompanyRegistryModal}
        onChange={handleCompanyRegistryFieldChange}
        onHide={closeCompanyRegistryModal}
        onSubmit={handleCompanyRegistrySubmit}
      />
      <LocationModal
        draft={locationDraft}
        fieldErrors={locationFieldErrors}
        saving={savingLocation}
        show={showLocationModal}
        onChange={handleLocationFieldChange}
        onHide={closeLocationModal}
        onSubmit={handleLocationSubmit}
      />
      <GroupModal
        draft={groupDraft}
        groups={groups}
        fieldErrors={groupFieldErrors}
        saving={savingGroup}
        show={showGroupModal}
        onChange={handleGroupFieldChange}
        onHide={closeGroupModal}
        onSubmit={handleGroupSubmit}
      />
      <UserModal
        draft={userDraft}
        groups={groups}
        fieldErrors={userFieldErrors}
        saving={savingUser}
        show={showUserModal}
        onChange={handleUserFieldChange}
        onGroupRoleChange={handleGroupAccessRoleChange}
        onHide={closeUserModal}
        onSubmit={handleUserSubmit}
      />
      <PasswordModal
        draft={passwordDraft}
        fieldErrors={passwordFieldErrors}
        saving={savingPassword}
        show={showPasswordModal}
        onChange={handlePasswordFieldChange}
        onHide={closePasswordModal}
        onSubmit={handleUserPasswordSubmit}
      />
    </Fragment>
  );
};

export default ClientsPage;
